import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../../Firebase';
import Swal from 'sweetalert2';
import {
  HiOutlineSquares2X2, HiOutlineShoppingCart, HiOutlineChatBubbleLeftRight,
  HiOutlineBriefcase, HiOutlineDocumentText, HiOutlinePower,
  HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineEye, HiOutlineArrowDownTray,
  HiOutlineCheckCircle, HiOutlineClock,
  HiOutlineUserGroup, HiOutlineChartBar, HiOutlineArrowLeft, HiOutlineUser
} from 'react-icons/hi2';
import { getMembers, saveMember, deleteMember, saveMembersOrder, DEFAULT_DIRECTOR_AVATAR, DEFAULT_LEADER_AVATAR } from '../../data/membersData';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa6';
import { getGrowthStats, saveGrowthStats } from '../../data/statsData';
import { getGalleryItems, saveGalleryItem, deleteGalleryItem, getGalleryCategories, saveGalleryCategory, deleteGalleryCategory } from '../../data/galleryData';
import { getJobOpenings, saveJobOpening, deleteJobOpening, getJobApplications, saveJobApplication, deleteJobApplication } from '../../data/careersData';
import { getProducts, saveProduct, deleteProductDoc } from '../../data/productsData';
import { getEnquiries, saveEnquiry, deleteEnquiryDoc } from '../../data/enquiriesData';
import {
  getDivisionCategories,
  saveDivisionCategory,
  deleteDivisionCategory,
  getDivisionProducts,
  saveDivisionProduct,
  deleteDivisionProduct
} from '../../data/divisionProducts';


export default function AdminPanel() {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [activeSubMenu, setActiveSubMenu] = useState('manage-products'); // for product management
  const [activeEnquiryTab, setActiveEnquiryTab] = useState('sales'); // for enquiry management
  const [activeDivisionTab, setActiveDivisionTab] = useState('solar'); // for divisions static editor
  const [selectedCategory, setSelectedCategory] = useState('Diesel Generators'); // for generator type toggle

  // Auth State
  const [user, setUser] = useState(null);

  // dynamic states for CRUD
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    id: '', name: '', category: 'Diesel Generators', image: '', desc: '', features: ['', '', ''], specs: { 'Power Rating': '', 'Voltage': '', 'Engine Make': '', 'Acoustic Level': '', 'Fuel Consumption': '' }
  });
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [isAddingGallery, setIsAddingGallery] = useState(false);

  // Division dynamic states
  const [divisionCategories, setDivisionCategories] = useState([]);
  const [divisionProducts, setDivisionProducts] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', desc: '' });
  const [editingDivProduct, setEditingDivProduct] = useState(null);
  const [newDivProduct, setNewDivProduct] = useState({ name: '', category: '', image: '', desc: '', specs: {} });
  const [isAddingDivProduct, setIsAddingDivProduct] = useState(false);
  const [customSpecs, setCustomSpecs] = useState([{ key: '', value: '' }]);

  // Profile Settings State
  const [profile, setProfile] = useState({ name: 'System Administrator', email: '' });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });

  // Dynamic Team Members State
  const [teamMembers, setTeamMembers] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '', role: '', tagline: '', image: '', bio: '', quote: '', type: 'leadership'
  });

  // Growth Stats State
  const [growthStats, setGrowthStats] = useState([]);

  // Gallery Management State
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [isAddingGalleryCat, setIsAddingGalleryCat] = useState(false);
  const [editingGalleryCat, setEditingGalleryCat] = useState(null);
  const [galleryCatForm, setGalleryCatForm] = useState({ name: '', slug: '' });
  const [galleryToggle, setGalleryToggle] = useState('images');
  const [newGalleryItem, setNewGalleryItem] = useState({
    title: '', desc: '', category: '', image: ''
  });
  const [isSavingGallery, setIsSavingGallery] = useState(false);

  // Careers Management State
  const [jobOpenings, setJobOpenings] = useState([]);
  const [newJobOpening, setNewJobOpening] = useState({
    title: '', location: '', dept: '', experience: '', desc: '', requirements: ['', '', '']
  });
  const [editingJobOpening, setEditingJobOpening] = useState(null);
  const [isAddingJobOpening, setIsAddingJobOpening] = useState(false);
  const [careerToggle, setCareerToggle] = useState('applications'); // 'applications' or 'postings'

  // Graph Hover State
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      const membersData = await getMembers();
      setTeamMembers(membersData);
      const statsData = await getGrowthStats();
      setGrowthStats(statsData);
      const galleryData = await getGalleryItems();
      setGalleryItems(galleryData);
      const galleryCats = await getGalleryCategories();
      setGalleryCategories(galleryCats);
      if (galleryCats.length > 0) {
        setNewGalleryItem(prev => ({ ...prev, category: galleryCats[0].slug }));
      }
      const openingsData = await getJobOpenings();
      setJobOpenings(openingsData);
      const appsData = await getJobApplications();
      setJobs(appsData);
      const prods = await getProducts();
      setProducts(prods);
      const enqs = await getEnquiries();
      setEnquiries(enqs);
    };
    loadData();
  }, []);

  const loadDivisionData = async (div) => {
    try {
      const cats = await getDivisionCategories(div);
      const prods = await getDivisionProducts(div);
      setDivisionCategories(cats);
      setDivisionProducts(prods);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadDivisionData(activeDivisionTab);
  }, [activeDivisionTab]);

  const [enquiries, setEnquiries] = useState([]);

  // Mock Job Applications
  const [jobs, setJobs] = useState([
    { id: 'job-1', name: 'Akash Kulkarni', email: 'akash.k@gmail.com', phone: '+91 88888 99999', position: 'Field Service Engineer (DG Sets)', experience: '4 Years', date: '2026-07-05 09:30', status: 'pending', resume: '/resumes/akash_kulkarni.pdf' },
    { id: 'job-2', name: 'Snehal Patil', email: 'snehal.patil@yahoo.com', phone: '+91 77777 66666', position: 'Solar Design & EPC Engineer', experience: '6 Years', date: '2026-07-04 11:20', status: 'shortlisted', resume: '/resumes/snehal_patil.pdf' },
    { id: 'job-3', name: 'Abhishek Shinde', email: 'abhishek@outlook.com', phone: '+91 99999 88888', position: 'Telecom Field Coordinator', experience: '2 Years', date: '2026-07-02 16:45', status: 'rejected', resume: '/resumes/abhishek_shinde.pdf' }
  ]);

  // Mock Recent Activities
  const [, setActivities] = useState([
    { id: 'act-1', text: 'Admin updated profile configurations.', time: '10 mins ago' },
    { id: 'act-2', text: 'New job application received from Akash Kulkarni.', time: '2 hours ago' },
    { id: 'act-3', text: 'New Telecom Service enquiry received from Jio NOC.', time: '4 hours ago' },
    { id: 'act-4', text: 'Generator Product "TMTL 125 kVA Silent" updated successfully.', time: '1 day ago' }
  ]);


  // Verify Firebase Auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setProfile(prev => ({
          ...prev,
          email: currentUser.email || '',
          name: currentUser.displayName || 'System Administrator'
        }));
      } else {
        sessionStorage.removeItem('admin_last_activity');
        navigate('/admin');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Session Inactivity (5 Minutes Timeout) & Browser Tab Close handling
  useEffect(() => {
    const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

    const handleAutoLogout = async (reason = 'inactivity') => {
      sessionStorage.removeItem('admin_last_activity');
      try {
        await signOut(auth);
      } catch (e) {
        // ignore signout error if already unauthenticated
      }

      if (reason === 'inactivity') {
        Swal.fire({
          title: 'Session Expired',
          text: 'You have been automatically logged out due to 5 minutes of inactivity.',
          icon: 'warning',
          confirmButtonColor: '#E05A17'
        });
      }
      navigate('/admin');
    };

    // Initialize or reset activity timestamp
    if (!sessionStorage.getItem('admin_last_activity')) {
      sessionStorage.setItem('admin_last_activity', Date.now().toString());
    }

    let throttleTimer = null;
    const updateActivity = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          sessionStorage.setItem('admin_last_activity', Date.now().toString());
          throttleTimer = null;
        }, 1000);
      }
    };

    // Check inactivity every 5 seconds
    const interval = setInterval(() => {
      const lastActivity = parseInt(sessionStorage.getItem('admin_last_activity') || Date.now().toString(), 10);
      if (Date.now() - lastActivity >= INACTIVITY_TIMEOUT) {
        handleAutoLogout('inactivity');
      }
    }, 5000);

    // Event listeners to detect activity
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(evt => window.addEventListener(evt, updateActivity));

    // Clear session if window/tab is closed
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('admin_last_activity');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      if (throttleTimer) clearTimeout(throttleTimer);
      activityEvents.forEach(evt => window.removeEventListener(evt, updateActivity));
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [navigate]);

  const handleLogout = async () => {
    const res = await Swal.fire({
      title: 'Sign Out?',
      text: 'Are you sure you want to end your administration session?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      cancelButtonColor: '#4a76b4',
      confirmButtonText: 'Yes, Sign Out'
    });
    if (res.isConfirmed) {
      sessionStorage.removeItem('admin_last_activity');
      await signOut(auth);
      Swal.fire('Logged Out', 'Your session has been terminated safely.', 'success');
      navigate('/admin');
    }
  };

  // Add Activity Log Helper
  const logActivity = (text) => {
    const newAct = { id: `act-${Date.now()}`, text, time: 'Just now' };
    setActivities(prev => [newAct, ...prev]);
  };

  // Change Password Auth Handler via Firebase
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      Swal.fire('Error', 'New passwords do not match.', 'error');
      return;
    }
    if (!passwords.new || passwords.new.length < 6) {
      Swal.fire('Weak Password', 'New password must be at least 6 characters.', 'warning');
      return;
    }
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      Swal.fire('Error', 'No authenticated Firebase user found.', 'error');
      return;
    }
    try {
      Swal.fire({
        title: 'Updating Password...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      const credential = EmailAuthProvider.credential(currentUser.email, passwords.current);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, passwords.new);
      Swal.fire({
        title: 'Password Updated',
        text: 'Your Firebase account password has been updated successfully.',
        icon: 'success',
        confirmButtonColor: '#0F5A3A'
      });
      logActivity('Administrator updated security password in Firebase.');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      console.error('Password update error:', err);
      let errMsg = err.message || 'Failed to update password.';
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        errMsg = 'Current password entered is incorrect.';
      }
      Swal.fire('Update Failed', errMsg, 'error');
    }
  };

  // Update Profile Settings Handler
  const handleUpdateProfile = (e) => {
    e.preventDefault();
    Swal.fire('Success', 'Profile settings updated successfully.', 'success');
    logActivity('Admin profile settings updated.');
  };


  // Product Image upload handler with 800 KB constraint
  const handleProductImageUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 800 * 1024) {
      Swal.fire({
        title: 'File Too Large',
        text: 'Your image file size must be less than 800 KB.',
        icon: 'warning',
        confirmButtonColor: '#E05A17'
      });
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      if (isEdit) {
        setEditingProduct(prev => ({ ...prev, image: evt.target.result }));
      } else {
        setNewProduct(prev => ({ ...prev, image: evt.target.result }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Product Management Features Helpers
  const handleNewProductFeatureChange = (index, value) => {
    const updatedFeatures = [...newProduct.features];
    updatedFeatures[index] = value;
    setNewProduct({ ...newProduct, features: updatedFeatures });
  };

  const handleAddNewProductFeatureField = () => {
    setNewProduct({ ...newProduct, features: [...newProduct.features, ''] });
  };

  const handleRemoveNewProductFeatureField = (index) => {
    const updatedFeatures = newProduct.features.filter((_, i) => i !== index);
    setNewProduct({ ...newProduct, features: updatedFeatures });
  };

  const handleEditProductFeatureChange = (index, value) => {
    const updatedFeatures = [...(editingProduct.features || [])];
    updatedFeatures[index] = value;
    setEditingProduct({ ...editingProduct, features: updatedFeatures });
  };

  const handleAddEditProductFeatureField = () => {
    const currentFeatures = editingProduct.features || [];
    setEditingProduct({ ...editingProduct, features: [...currentFeatures, ''] });
  };

  const handleRemoveEditProductFeatureField = (index) => {
    const currentFeatures = editingProduct.features || [];
    const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
    setEditingProduct({ ...editingProduct, features: updatedFeatures });
  };

  // Product Management CRUD Database Operations
  const handleAddProduct = async (e) => {
    e.preventDefault();
    const id = `prod-${Date.now()}`;
    const productToAdd = { ...newProduct, id };
    try {
      Swal.fire({
        title: 'Saving your data...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveProduct(productToAdd);
      const updated = await getProducts();
      setProducts(updated);
      logActivity(`Added new Generator Product: ${newProduct.name}`);
      Swal.fire('Updated', 'Your data is modified.', 'success');
      setNewProduct({
        id: '', name: '', category: 'Diesel Generators', image: '', desc: '', features: ['', '', ''], specs: { 'Power Rating': '', 'Voltage': '', 'Engine Make': '', 'Acoustic Level': '', 'Fuel Consumption': '' }
      });
      setActiveSubMenu('manage-products');
    } catch (err) {
      Swal.fire('Error', 'Failed to add product.', 'error');
    }
  };

  const handleEditProductClick = (prod) => {
    setEditingProduct({ ...prod });
    setActiveSubMenu('edit-generator');
  };

  const handleSaveProductEdit = async (e) => {
    e.preventDefault();
    try {
      Swal.fire({
        title: 'Saving your data...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveProduct(editingProduct);
      const updated = await getProducts();
      setProducts(updated);
      Swal.fire('Updated', 'Your data is modified.', 'success');
      logActivity(`Updated Generator Product: ${editingProduct.name}`);
      setEditingProduct(null);
      setActiveSubMenu('manage-products');
    } catch (err) {
      Swal.fire('Error', 'Failed to update product.', 'error');
    }
  };

  const handleDeleteProduct = (id, name) => {
    Swal.fire({
      title: 'Delete Product?',
      text: `Are you sure you want to permanently remove "${name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          Swal.fire({
            title: 'Saving your data...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
          });
          await deleteProductDoc(id);
          const updated = await getProducts();
          setProducts(updated);
          Swal.fire('Deleted', 'Your data is modified.', 'success');
          logActivity(`Deleted Generator Product: ${name}`);
        } catch (err) {
          Swal.fire('Error', 'Failed to delete product.', 'error');
        }
      }
    });
  };

  // Enquiry Update & Delete Actions
  const handleUpdateEnquiryStatus = async (id, newStatus) => {
    const updated = enquiries.map(e => e.id === id ? { ...e, status: newStatus } : e);
    setEnquiries(updated);
    const target = updated.find(e => e.id === id);
    if (target) {
      try {
        await saveEnquiry(target);
        logActivity(`Enquiry status changed to ${newStatus} for ID: ${id}`);
        Swal.fire('Updated', 'Your data is modified.', 'success');
      } catch (err) {
        Swal.fire('Error', 'Failed to update status.', 'error');
      }
    }
  };

  const handleDeleteEnquiry = (id, clientName) => {
    Swal.fire({
      title: 'Delete Enquiry?',
      text: `Are you sure you want to permanently delete the enquiry from "${clientName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          Swal.fire({
            title: 'Saving your data...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
          });
          await deleteEnquiryDoc(id);
          const updated = await getEnquiries();
          setEnquiries(updated);
          Swal.fire('Deleted', 'Your data is modified.', 'success');
          logActivity(`Deleted enquiry from: ${clientName}`);
        } catch (err) {
          Swal.fire('Error', 'Failed to delete enquiry.', 'error');
        }
      }
    });
  };

  const handleSaveGalleryCategory = async (e) => {
    e.preventDefault();
    if (!galleryCatForm.name.trim()) return;
    const slug = galleryCatForm.slug.trim() || galleryCatForm.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
    const catToSave = {
      id: editingGalleryCat ? editingGalleryCat.id : `cat-gal-${Date.now()}`,
      name: galleryCatForm.name.trim(),
      slug: slug
    };
    try {
      await saveGalleryCategory(catToSave);
      const cats = await getGalleryCategories();
      setGalleryCategories(cats);
      setIsAddingGalleryCat(false);
      setEditingGalleryCat(null);
      setGalleryCatForm({ name: '', slug: '' });
      Swal.fire('Success', 'Gallery category saved successfully.', 'success');
      logActivity(`Saved Gallery Category: ${catToSave.name}`);
    } catch (err) {
      Swal.fire('Error', 'Failed to save category.', 'error');
    }
  };

  const handleDeleteGalleryCategory = async (id, name) => {
    const res = await Swal.fire({
      title: 'Delete Category?',
      text: `Are you sure you want to delete "${name}"? This won't delete the images but their category classification might be affected.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    });
    if (res.isConfirmed) {
      try {
        await deleteGalleryCategory(id);
        const cats = await getGalleryCategories();
        setGalleryCategories(cats);
        Swal.fire('Deleted', 'Gallery category deleted successfully.', 'success');
        logActivity(`Deleted Gallery Category: ${name}`);
      } catch (err) {
        Swal.fire('Error', 'Failed to delete category.', 'error');
      }
    }
  };

  // Job status Update Actions
  const handleUpdateJobStatus = async (id, newStatus) => {
    const updatedJobs = jobs.map(j => j.id === id ? { ...j, status: newStatus } : j);
    setJobs(updatedJobs);
    const targetJob = updatedJobs.find(j => j.id === id);
    if (targetJob) {
      try {
        await saveJobApplication(targetJob);
        logActivity(`Job Application ID: ${id} status set to ${newStatus}`);
        Swal.fire('Applicant Updated', `Status updated to ${newStatus}.`, 'success');
      } catch (err) {
        Swal.fire('Error', 'Failed to update application status in database.', 'error');
      }
    }
  };

  // Real PDF View & Download Actions
  const handleViewResume = (name, base64Data) => {
    if (!base64Data) {
      Swal.fire('No Resume', 'No PDF resume file found for this applicant.', 'warning');
      return;
    }
    const newTab = window.open();
    if (newTab) {
      newTab.document.write(
        `<iframe src="${base64Data}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`
      );
      newTab.document.title = `Resume: ${name}`;
    } else {
      Swal.fire('Popup Blocked', 'Please allow popups in your browser to view the PDF resume.', 'warning');
    }
  };

  const handleDownloadResume = (name, base64Data, fileName) => {
    if (!base64Data) {
      Swal.fire('No Resume', 'No PDF resume file found for this applicant.', 'warning');
      return;
    }
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = fileName || `${name.replace(/\s+/g, '_')}_Resume.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    Swal.fire('Download Triggered', 'Your download for the PDF resume has started.', 'success');
  };

  const handleDeleteJobApplication = (id, applicantName) => {
    Swal.fire({
      title: 'Delete Application?',
      text: `Are you sure you want to permanently delete the application of "${applicantName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          Swal.fire({
            title: 'Deleting Application...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
          });
          await deleteJobApplication(id);
          const updated = await getJobApplications();
          setJobs(updated);
          Swal.fire('Deleted', 'Application record has been removed.', 'success');
          logActivity(`Deleted job application from: ${applicantName}`);
        } catch (err) {
          Swal.fire('Error', 'Failed to delete application.', 'error');
        }
      }
    });
  };

  // Job Opening CRUD Handlers
  const handleAddJobOpening = async (e) => {
    e.preventDefault();
    const id = `op-${Date.now()}`;
    const filteredRequirements = newJobOpening.requirements.filter(r => r.trim() !== '');
    const opToAdd = { ...newJobOpening, id, requirements: filteredRequirements };

    try {
      Swal.fire({
        title: 'Posting Job Opening...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveJobOpening(opToAdd);
      const updated = await getJobOpenings();
      setJobOpenings(updated);
      Swal.fire('Job Posted', `"${newJobOpening.title}" opening has been published.`, 'success');
      logActivity(`Posted new job opening: ${newJobOpening.title}`);
      setNewJobOpening({ title: '', location: '', dept: '', experience: '', desc: '', requirements: ['', '', ''] });
      setIsAddingJobOpening(false);
    } catch (err) {
      Swal.fire('Error', 'Failed to post job opening.', 'error');
    }
  };

  const handleEditJobOpeningClick = (op) => {
    setEditingJobOpening({ ...op, requirements: op.requirements && op.requirements.length > 0 ? [...op.requirements] : ['', '', ''] });
  };

  const handleSaveJobOpeningEdit = async (e) => {
    e.preventDefault();
    const filteredRequirements = editingJobOpening.requirements.filter(r => r.trim() !== '');
    const opToSave = { ...editingJobOpening, requirements: filteredRequirements };

    try {
      Swal.fire({
        title: 'Saving Job Details...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveJobOpening(opToSave);
      const updated = await getJobOpenings();
      setJobOpenings(updated);
      Swal.fire('Job Updated', `Changes for "${editingJobOpening.title}" have been saved.`, 'success');
      logActivity(`Updated job details for: ${editingJobOpening.title}`);
      setEditingJobOpening(null);
    } catch (err) {
      Swal.fire('Error', 'Failed to update job details.', 'error');
    }
  };

  const handleDeleteJobOpening = (id, title) => {
    Swal.fire({
      title: 'Delete Job Post?',
      text: `Are you sure you want to permanently remove "${title}"? This will also remove it from the public careers page.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          Swal.fire({
            title: 'Removing Job Post...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
          });
          await deleteJobOpening(id);
          const updated = await getJobOpenings();
          setJobOpenings(updated);
          Swal.fire('Deleted', 'The job posting has been removed.', 'success');
          logActivity(`Deleted job posting: ${title}`);
        } catch (err) {
          Swal.fire('Error', 'Failed to delete job posting.', 'error');
        }
      }
    });
  };

  const handleRequirementChange = (idx, value, isEdit = false) => {
    if (isEdit) {
      setEditingJobOpening(prev => {
        const reqs = [...prev.requirements];
        reqs[idx] = value;
        return { ...prev, requirements: reqs };
      });
    } else {
      setNewJobOpening(prev => {
        const reqs = [...prev.requirements];
        reqs[idx] = value;
        return { ...prev, requirements: reqs };
      });
    }
  };

  const addRequirementInput = (isEdit = false) => {
    if (isEdit) {
      setEditingJobOpening(prev => ({ ...prev, requirements: [...prev.requirements, ''] }));
    } else {
      setNewJobOpening(prev => ({ ...prev, requirements: [...prev.requirements, ''] }));
    }
  };

  const removeRequirementInput = (idx, isEdit = false) => {
    if (isEdit) {
      setEditingJobOpening(prev => ({ ...prev, requirements: prev.requirements.filter((_, i) => i !== idx) }));
    } else {
      setNewJobOpening(prev => ({ ...prev, requirements: prev.requirements.filter((_, i) => i !== idx) }));
    }
  };

  // Member CRUD actions
  const handleAddMember = async (e) => {
    e.preventDefault();

    // Mandatory field validations
    if (!newMember.name || !newMember.name.trim()) {
      Swal.fire('Full Name Required', 'Please enter the full name of the member.', 'warning');
      return;
    }
    if (!newMember.role || !newMember.role.trim()) {
      Swal.fire('Designation Required', 'Please enter the designation or role of the member.', 'warning');
      return;
    }
    if (!newMember.type) {
      Swal.fire('Member Type Required', 'Please select a member type.', 'warning');
      return;
    }
    if (!newMember.image || !newMember.image.trim()) {
      Swal.fire({
        title: 'Photo Required',
        text: 'Uploading a member photo is mandatory. Please choose and upload a photo before saving.',
        icon: 'warning',
        confirmButtonColor: '#E05A17'
      });
      return;
    }
    if (newMember.type === 'director') {
      if (!newMember.bio || !newMember.bio.trim()) {
        Swal.fire('Bio Required', 'Please enter the description / bio for the Director.', 'warning');
        return;
      }
      if (!newMember.quote || !newMember.quote.trim()) {
        Swal.fire('Quote Required', 'Please enter a leadership quote statement for the Director.', 'warning');
        return;
      }
    }

    const id = `member-${Date.now()}`;
    const memberToAdd = {
      ...newMember,
      id,
      name: newMember.name.trim(),
      role: newMember.role.trim(),
      bio: newMember.bio ? newMember.bio.trim() : '',
      quote: newMember.quote ? newMember.quote.trim() : ''
    };
    try {
      Swal.fire({
        title: 'Saving your data...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveMember(memberToAdd);
      const updated = await getMembers();
      setTeamMembers(updated);
      Swal.fire('Updated', 'New member saved successfully.', 'success');
      logActivity(`Added new team member: ${memberToAdd.name}`);
      setNewMember({
        name: '', role: '', tagline: '', image: '', bio: '', quote: '', type: 'leadership'
      });
      setIsAddingMember(false);
    } catch (err) {
      Swal.fire('Error', 'Failed to save member.', 'error');
    }
  };

  const handleEditMemberClick = (member) => {
    setEditingMember({ ...member });
  };

  const handleSaveMemberEdit = async (e) => {
    e.preventDefault();
    if (!editingMember) return;

    // Mandatory field validations for edit
    if (!editingMember.name || !editingMember.name.trim()) {
      Swal.fire('Full Name Required', 'Please enter the full name of the member.', 'warning');
      return;
    }
    if (!editingMember.role || !editingMember.role.trim()) {
      Swal.fire('Designation Required', 'Please enter the designation or role of the member.', 'warning');
      return;
    }
    if (!editingMember.image || !editingMember.image.trim()) {
      Swal.fire({
        title: 'Photo Required',
        text: 'A member photo is mandatory. Please upload a photo before saving.',
        icon: 'warning',
        confirmButtonColor: '#E05A17'
      });
      return;
    }
    if (editingMember.type === 'director') {
      if (!editingMember.bio || !editingMember.bio.trim()) {
        Swal.fire('Bio Required', 'Please enter the description / bio for the Director.', 'warning');
        return;
      }
      if (!editingMember.quote || !editingMember.quote.trim()) {
        Swal.fire('Quote Required', 'Please enter a leadership quote statement for the Director.', 'warning');
        return;
      }
    }

    try {
      Swal.fire({
        title: 'Saving your data...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveMember({
        ...editingMember,
        name: editingMember.name.trim(),
        role: editingMember.role.trim(),
        bio: editingMember.bio ? editingMember.bio.trim() : '',
        quote: editingMember.quote ? editingMember.quote.trim() : ''
      });
      const updated = await getMembers();
      setTeamMembers(updated);
      Swal.fire('Updated', 'Your data is modified.', 'success');
      logActivity(`Updated details for team member: ${editingMember.name}`);
      setEditingMember(null);
    } catch (err) {
      Swal.fire('Error', 'Failed to update member.', 'error');
    }
  };

  const handleDeleteMember = (id, name) => {
    Swal.fire({
      title: 'Delete Member?',
      text: `Are you sure you want to permanently remove "${name}" from the board?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          Swal.fire({
            title: 'Saving your data...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
          });
          await deleteMember(id);
          const updated = await getMembers();
          setTeamMembers(updated);
          Swal.fire('Deleted', 'Your data is modified.', 'success');
          logActivity(`Deleted team member: ${name}`);
        } catch (err) {
          Swal.fire('Error', 'Failed to delete member.', 'error');
        }
      }
    });
  };

  const handleMoveMember = async (memberId, direction) => {
    // Get non-director members in their current sorted order
    const currentList = [...teamMembers];
    // Sort them exactly as getMembers() does to ensure we have the correct visual order
    currentList.sort((a, b) => {
      if (a.type === 'director' && b.type !== 'director') return -1;
      if (a.type !== 'director' && b.type === 'director') return 1;
      const orderA = a.order !== undefined ? a.order : 999;
      const orderB = b.order !== undefined ? b.order : 999;
      if (orderA !== orderB) return orderA - orderB;
      return a.id.localeCompare(b.id);
    });

    const leadershipMembers = currentList.filter(m => m.type !== 'director');
    const idx = leadershipMembers.findIndex(m => m.id === memberId);
    if (idx === -1) return;

    const isMovingUp = direction === 'up' && idx > 0;
    const isMovingDown = direction === 'down' && idx < leadershipMembers.length - 1;

    if (isMovingUp || isMovingDown) {
      const targetIdx = isMovingUp ? idx - 1 : idx + 1;

      // Initialize orders dynamically by assigning sequential indices to ALL leadership members
      leadershipMembers.forEach((m, i) => {
        m.order = i;
      });

      // Swap the order indices of the target and selected members
      const tempOrder = leadershipMembers[idx].order;
      leadershipMembers[idx].order = leadershipMembers[targetIdx].order;
      leadershipMembers[targetIdx].order = tempOrder;

      // Save all updated members via batch write
      try {
        Swal.fire({
          title: 'Updating hierarchy...',
          allowOutsideClick: false,
          didOpen: () => { Swal.showLoading(); }
        });
        await saveMembersOrder(leadershipMembers);
        // Refresh state
        const updated = await getMembers();
        setTeamMembers(updated);
        Swal.fire('Success', 'Staff order updated.', 'success');
      } catch (err) {
        Swal.fire('Error', 'Failed to save new order.', 'error');
      }
    }
  };

  const handleMemberPhotoUpload = (e, callback) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // File size constraint: Max 10 MB (will be compressed via canvas)
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire({
        title: 'File Too Large',
        text: 'The uploaded photo exceeds 10 MB. Please upload a smaller image file.',
        icon: 'error',
        confirmButtonColor: '#E05A17'
      });
      e.target.value = '';
      return;
    }

    Swal.fire({
      title: 'Processing photo...',
      text: 'Optimizing and preparing image for upload',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); }
    });

    const reader = new FileReader();
    reader.onload = (evt) => {
      const img = new Image();
      img.src = evt.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 600;
        const MAX_HEIGHT = 750;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round(height * (MAX_WIDTH / width));
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round(width * (MAX_HEIGHT / height));
            height = MAX_HEIGHT;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
        Swal.close();
        callback(dataUrl);
      };
      img.onerror = () => {
        Swal.fire('Image Error', 'Failed to load the selected image file.', 'error');
      };
    };
    reader.onerror = () => {
      Swal.fire('File Error', 'Failed to read the selected file.', 'error');
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateStatField = (idx, field, value) => {
    setGrowthStats(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleSaveGrowthStats = async (e) => {
    e.preventDefault();
    try {
      Swal.fire({
        title: 'Saving your data...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveGrowthStats(growthStats);
      Swal.fire('Updated', 'Your data is modified.', 'success');
      logActivity('Updated dynamic growth stats configurations.');
    } catch (err) {
      Swal.fire('Error', 'Failed to save stats.', 'error');
    }
  };

  // Gallery CRUD Handlers
  // Gallery Photo upload handler with 800 KB limit
  const handleGalleryPhotoUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    // Max size: 800 KB
    if (file.size > 800 * 1024) {
      Swal.fire({
        title: 'File Too Large',
        text: 'The uploaded photo exceeds the 800 KB size limit. Please upload a smaller image file.',
        icon: 'warning',
        confirmButtonColor: '#E05A17'
      });
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      if (isEdit) {
        setEditingGalleryItem(prev => ({ ...prev, image: evt.target.result }));
      } else {
        setNewGalleryItem(prev => ({ ...prev, image: evt.target.result }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddGalleryItem = async (e) => {
    e.preventDefault();
    if (!newGalleryItem.image) {
      Swal.fire('Photo Required', 'Please upload a photo for the gallery item.', 'warning');
      return;
    }

    setIsSavingGallery(true);
    const id = `gal-${Date.now()}`;
    const itemToAdd = { ...newGalleryItem, id };

    try {
      Swal.fire({
        title: 'Saving your data...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveGalleryItem(itemToAdd);
      const updated = await getGalleryItems();
      setGalleryItems(updated);
      Swal.fire('Updated', 'Your data is modified.', 'success');
      logActivity(`Added new gallery image: ${newGalleryItem.title}`);
      setNewGalleryItem({ title: '', desc: '', category: 'sales', image: '' });
      setIsAddingGallery(false);
      const fileInput = document.getElementById('gallery-file-input');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      Swal.fire('Error', 'Failed to save gallery item.', 'error');
    } finally {
      setIsSavingGallery(false);
    }
  };

  const handleEditGalleryItemClick = (item) => {
    setEditingGalleryItem({ ...item });
  };

  const handleSaveGalleryEdit = async (e) => {
    e.preventDefault();
    if (!editingGalleryItem.image) {
      Swal.fire('Photo Required', 'Please upload a photo for the gallery item.', 'warning');
      return;
    }

    setIsSavingGallery(true);
    try {
      Swal.fire({
        title: 'Saving your data...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      await saveGalleryItem(editingGalleryItem);
      const updated = await getGalleryItems();
      setGalleryItems(updated);
      Swal.fire('Updated', 'Your data is modified.', 'success');
      logActivity(`Edited gallery item details for: ${editingGalleryItem.title}`);
      setEditingGalleryItem(null);
    } catch (err) {
      Swal.fire('Error', 'Failed to save changes.', 'error');
    } finally {
      setIsSavingGallery(false);
    }
  };

  const handleDeleteGalleryItem = (id, title) => {
    Swal.fire({
      title: 'Delete Image?',
      text: `Are you sure you want to permanently remove "${title}" from the gallery?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          Swal.fire({
            title: 'Saving your data...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
          });
          await deleteGalleryItem(id);
          const updated = await getGalleryItems();
          setGalleryItems(updated);
          Swal.fire('Deleted', 'Your data is modified.', 'success');
          logActivity(`Deleted gallery image: ${title}`);
        } catch (err) {
          Swal.fire('Error', 'Failed to delete gallery item.', 'error');
        }
      }
    });
  };

  // Division Management: Categories CRUD Handlers
  const handleAddDivisionCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) return;
    const catId = `cat-${activeDivisionTab}-${Date.now()}`;
    const categoryToAdd = {
      id: catId,
      division: activeDivisionTab,
      name: newCategory.name,
      desc: newCategory.desc
    };

    try {
      Swal.fire({ title: 'Saving Category...', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
      await saveDivisionCategory(categoryToAdd);
      await loadDivisionData(activeDivisionTab);
      setNewCategory({ name: '', desc: '' });
      logActivity(`Created new category "${categoryToAdd.name}" in ${activeDivisionTab}`);
      Swal.fire('Success', 'Category created successfully.', 'success');
    } catch (err) {
      Swal.fire('Error', 'Failed to add category.', 'error');
    }
  };

  const handleSaveDivisionCategoryEdit = async (e) => {
    e.preventDefault();
    if (!editingCategory || !editingCategory.name.trim()) return;
    try {
      Swal.fire({ title: 'Saving Category...', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
      await saveDivisionCategory(editingCategory);
      await loadDivisionData(activeDivisionTab);
      logActivity(`Updated category "${editingCategory.name}" in ${activeDivisionTab}`);
      setEditingCategory(null);
      Swal.fire('Success', 'Category updated successfully.', 'success');
    } catch (err) {
      Swal.fire('Error', 'Failed to update category.', 'error');
    }
  };

  const handleDeleteDivisionCategory = async (id, name) => {
    const res = await Swal.fire({
      title: 'Delete Category?',
      text: `Are you sure you want to permanently delete category "${name}"? This will not delete products assigned to this category.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    });
    if (res.isConfirmed) {
      try {
        Swal.fire({ title: 'Deleting Category...', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
        await deleteDivisionCategory(id);
        await loadDivisionData(activeDivisionTab);
        logActivity(`Deleted category "${name}" in ${activeDivisionTab}`);
        Swal.fire('Deleted', 'Category removed.', 'success');
      } catch (err) {
        Swal.fire('Error', 'Failed to delete category.', 'error');
      }
    }
  };

  // Division Management: Products CRUD Handlers
  const handleDivProductImageUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 800 * 1024) {
      Swal.fire({
        title: 'File Too Large',
        text: 'Your image file size must be less than 800 KB.',
        icon: 'warning',
        confirmButtonColor: '#E05A17'
      });
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      if (isEdit) {
        setEditingDivProduct(prev => ({ ...prev, image: evt.target.result }));
      } else {
        setNewDivProduct(prev => ({ ...prev, image: evt.target.result }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddDivProduct = async (e) => {
    e.preventDefault();
    if (!newDivProduct.name.trim()) {
      Swal.fire('Name Required', 'Product name is required.', 'warning');
      return;
    }

    const specsObj = {};
    customSpecs.forEach(spec => {
      if (spec.key.trim()) {
        specsObj[spec.key.trim()] = spec.value;
      }
    });

    const prodId = `prod-${activeDivisionTab}-${Date.now()}`;
    const productToAdd = {
      ...newDivProduct,
      id: prodId,
      division: activeDivisionTab,
      category: newDivProduct.category || (divisionCategories[0] ? divisionCategories[0].name : ''),
      specs: specsObj
    };

    try {
      Swal.fire({ title: 'Saving Product...', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
      await saveDivisionProduct(productToAdd);
      await loadDivisionData(activeDivisionTab);
      logActivity(`Created new product "${productToAdd.name}" in ${activeDivisionTab}`);
      setNewDivProduct({ name: '', category: divisionCategories[0]?.name || '', image: '', desc: '', specs: {} });
      setCustomSpecs([{ key: '', value: '' }]);
      setIsAddingDivProduct(false);
      Swal.fire('Success', 'Product added successfully.', 'success');
    } catch (err) {
      Swal.fire('Error', 'Failed to add product.', 'error');
    }
  };

  const handleEditDivProductClick = (prod) => {
    setEditingDivProduct({ ...prod });
    const specsArr = Object.entries(prod.specs || {}).map(([key, value]) => ({ key, value }));
    setCustomSpecs(specsArr.length > 0 ? specsArr : [{ key: '', value: '' }]);
    setIsAddingDivProduct(true);
  };

  const handleSaveDivProductEdit = async (e) => {
    e.preventDefault();
    if (!editingDivProduct || !editingDivProduct.name.trim()) return;

    const specsObj = {};
    customSpecs.forEach(spec => {
      if (spec.key.trim()) {
        specsObj[spec.key.trim()] = spec.value;
      }
    });

    const productToSave = {
      ...editingDivProduct,
      specs: specsObj
    };

    try {
      Swal.fire({ title: 'Saving Product...', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
      await saveDivisionProduct(productToSave);
      await loadDivisionData(activeDivisionTab);
      logActivity(`Updated product "${productToSave.name}" in ${activeDivisionTab}`);
      setEditingDivProduct(null);
      setCustomSpecs([{ key: '', value: '' }]);
      setIsAddingDivProduct(false);
      Swal.fire('Success', 'Product updated successfully.', 'success');
    } catch (err) {
      Swal.fire('Error', 'Failed to update product.', 'error');
    }
  };

  const handleDeleteDivProduct = async (id, name) => {
    const res = await Swal.fire({
      title: 'Delete Product?',
      text: `Are you sure you want to permanently delete "${name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E05A17',
      confirmButtonText: 'Yes, Delete'
    });
    if (res.isConfirmed) {
      try {
        Swal.fire({ title: 'Deleting Product...', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
        await deleteDivisionProduct(id);
        await loadDivisionData(activeDivisionTab);
        logActivity(`Deleted product "${name}" in ${activeDivisionTab}`);
        Swal.fire('Deleted', 'Product removed.', 'success');
      } catch (err) {
        Swal.fire('Error', 'Failed to delete product.', 'error');
      }
    }
  };

  const handleSpecChange = (index, field, val) => {
    setCustomSpecs(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: val };
      return updated;
    });
  };

  const addSpecField = () => {
    setCustomSpecs(prev => [...prev, { key: '', value: '' }]);
  };

  const removeSpecField = (index) => {
    setCustomSpecs(prev => prev.filter((_, i) => i !== index));
  };

  // Calculations for Metrics & Enquiries Graph
  const totalEnquiries = enquiries.length;
  const dieselCount = products.filter(p => p.category === 'Diesel Generators').length;
  const silentCount = products.filter(p => p.category === 'Silent Generators').length;
  const airCooledCount = products.filter(p => p.category === 'Air-cooled Generators').length;
  const totalGenerators = products.length;

  const totalJobsPosted = jobOpenings.length;
  const totalJobApplications = jobs.length;

  const totalPhotos = galleryItems.length;

  // Helper to dynamically get last 6 months starting from current month
  const getMonthsList = () => {
    const list = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = d.getFullYear();
      const monthNum = String(d.getMonth() + 1).padStart(2, '0');
      const label = d.toLocaleString('default', { month: 'short' });
      const yearLabel = String(d.getFullYear()).slice(-2);
      list.push({
        year,
        monthNum,
        label: `${label} '${yearLabel}`,
        key: `${year}-${monthNum}`
      });
    }
    return list;
  };

  const monthsList = getMonthsList();
  const enquiriesCountByMonth = monthsList.map(m => {
    const count = enquiries.filter(enq => enq.date && enq.date.startsWith(m.key)).length;
    return { ...m, count };
  });

  const maxY = Math.max(...enquiriesCountByMonth.map(m => m.count), 4);
  const plotWidth = 530;
  const plotHeight = 160;
  const paddingLeft = 45;
  const paddingTop = 20;

  const points = enquiriesCountByMonth.map((m, idx) => {
    const x = paddingLeft + (idx / 5) * plotWidth;
    const y = paddingTop + plotHeight - (m.count / maxY) * plotHeight;
    return { x, y, ...m, index: idx };
  });

  const areaPath = points.length > 0
    ? `M ${points[0].x} ${paddingTop + plotHeight} ` +
    points.map(p => `L ${p.x} ${p.y}`).join(' ') +
    ` L ${points[points.length - 1].x} ${paddingTop + plotHeight} Z`
    : '';

  const linePath = points.length > 0
    ? `M ${points[0].x} ${points[0].y} ` +
    points.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ')
    : '';

  const gridLines = [];
  for (let i = 0; i <= maxY; i++) {
    const y = paddingTop + plotHeight - (i / maxY) * plotHeight;
    gridLines.push(
      <g key={i}>
        <line
          x1={paddingLeft}
          y1={y}
          x2={paddingLeft + plotWidth}
          y2={y}
          stroke="#E2E8F0"
          strokeWidth="1"
          strokeDasharray="4 4"
        />
        <text
          x={paddingLeft - 12}
          y={y + 4}
          textAnchor="end"
          className="text-[10px] font-bold fill-slate-400"
        >
          {i}
        </text>
      </g>
    );
  }

  if (!user) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600">Loading Admin Session...</div>;

  return (
    <div className="h-screen overflow-hidden bg-slate-50 text-slate-900 flex font-sans antialiased text-sm">

      {/* SIDEBAR NAVIGATION PANEL */}
      <aside className="w-72 bg-slate-950 border-r border-slate-900 flex flex-col flex-shrink-0 z-20 shadow-xl">
        <div className="p-6 border-b border-slate-900 flex items-center justify-center bg-slate-950">
          <div className="bg-white px-4 py-3 rounded-2xl shadow-md border border-slate-800/10 flex items-center justify-center w-full max-w-[220px]">
            <img src="/Green Gusto.png" alt="Admin Logo" className="h-14 w-auto object-contain mx-auto" />
          </div>
        </div>

        <nav className="flex-grow p-5 space-y-4 overflow-y-auto">
          {/* Dashboard */}
          <button
            onClick={() => setActiveMenu('dashboard')}
            className={`w-full flex items-center justify-center gap-3.5 px-4.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border ${activeMenu === 'dashboard'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/10 border-emerald-500/20 scale-[1.03]'
                : 'text-slate-400 hover:text-white bg-slate-900/30 hover:bg-slate-900 border-transparent hover:border-slate-800 hover:scale-[1.01] hover:translate-y-[-1px]'
              }`}
          >
            <HiOutlineSquares2X2 className="w-5 h-5 flex-shrink-0" />
            Dashboard
          </button>

          {/* Product Management */}
          <button
            onClick={() => {
              setActiveMenu('products');
              setActiveSubMenu('manage-products');
            }}
            className={`w-full flex items-center justify-center gap-3.5 px-4.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border ${activeMenu === 'products'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/10 border-emerald-500/20 scale-[1.03]'
                : 'text-slate-400 hover:text-white bg-slate-900/30 hover:bg-slate-900 border-transparent hover:border-slate-800 hover:scale-[1.01] hover:translate-y-[-1px]'
              }`}
          >
            <HiOutlineShoppingCart className="w-5 h-5 flex-shrink-0" />
            Product Management
          </button>

          {/* Enquiry Management */}
          <button
            onClick={() => setActiveMenu('enquiries')}
            className={`w-full flex items-center justify-center gap-3.5 px-4.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border ${activeMenu === 'enquiries'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/10 border-emerald-500/20 scale-[1.03]'
                : 'text-slate-400 hover:text-white bg-slate-900/30 hover:bg-slate-900 border-transparent hover:border-slate-800 hover:scale-[1.01] hover:translate-y-[-1px]'
              }`}
          >
            <HiOutlineChatBubbleLeftRight className="w-5 h-5 flex-shrink-0" />
            Enquiry Management
          </button>

          {/* Career Management */}
          <button
            onClick={() => setActiveMenu('careers')}
            className={`w-full flex items-center justify-center gap-3.5 px-4.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border ${activeMenu === 'careers'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/10 border-emerald-500/20 scale-[1.03]'
                : 'text-slate-400 hover:text-white bg-slate-900/30 hover:bg-slate-900 border-transparent hover:border-slate-800 hover:scale-[1.01] hover:translate-y-[-1px]'
              }`}
          >
            <HiOutlineBriefcase className="w-5 h-5 flex-shrink-0" />
            Career Management
          </button>

          {/* Divisions Editable Pages */}
          <button
            onClick={() => setActiveMenu('divisions')}
            className={`w-full flex items-center justify-center gap-3.5 px-4.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border ${activeMenu === 'divisions'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/10 border-emerald-500/20 scale-[1.03]'
                : 'text-slate-400 hover:text-white bg-slate-900/30 hover:bg-slate-900 border-transparent hover:border-slate-800 hover:scale-[1.01] hover:translate-y-[-1px]'
              }`}
          >
            <HiOutlineDocumentText className="w-5 h-5 flex-shrink-0" />
            Divisions Management
          </button>

          {/* Manage Members */}
          <button
            onClick={() => setActiveMenu('members')}
            className={`w-full flex items-center justify-center gap-3.5 px-4.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border ${activeMenu === 'members'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/10 border-emerald-500/20 scale-[1.03]'
                : 'text-slate-400 hover:text-white bg-slate-900/30 hover:bg-slate-900 border-transparent hover:border-slate-800 hover:scale-[1.01] hover:translate-y-[-1px]'
              }`}
          >
            <HiOutlineUserGroup className="w-5 h-5 flex-shrink-0" />
            Manage Members
          </button>

          {/* Manage Gallery */}
          <button
            onClick={() => setActiveMenu('gallery')}
            className={`w-full flex items-center justify-center gap-3.5 px-4.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border ${activeMenu === 'gallery'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/10 border-emerald-500/20 scale-[1.03]'
                : 'text-slate-400 hover:text-white bg-slate-900/30 hover:bg-slate-900 border-transparent hover:border-slate-800 hover:scale-[1.01] hover:translate-y-[-1px]'
              }`}
          >
            <HiOutlineEye className="w-5 h-5 flex-shrink-0" />
            Manage Gallery
          </button>

          {/* Growth Stats */}
          <button
            onClick={() => setActiveMenu('stats')}
            className={`w-full flex items-center justify-center gap-3.5 px-4.5 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border ${activeMenu === 'stats'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/10 border-emerald-500/20 scale-[1.03]'
                : 'text-slate-400 hover:text-white bg-slate-900/30 hover:bg-slate-900 border-transparent hover:border-slate-800 hover:scale-[1.01] hover:translate-y-[-1px]'
              }`}
          >
            <HiOutlineChartBar className="w-5 h-5 flex-shrink-0" />
            Growth Stats
          </button>
        </nav>
      </aside>

      {/* MAIN VIEW CONTENT CONTAINER */}
      <main className="flex-grow p-10 md:p-12 overflow-y-auto bg-slate-50 relative flex flex-col">

        {/* TOP HEADER ROW */}
        <div className="flex justify-end items-center mb-8 pb-4 border-b border-slate-200 flex-shrink-0">

          {/* Split Profile & Logout Cards (Horizontally Side-by-Side) */}
          <div className="flex items-center gap-3.5 -translate-y-3">
            {/* Card 1: Profile Details */}
            <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-2.5 shadow-sm">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/10 shadow-inner">
                <HiOutlineUser className="w-4 h-4" />
              </div>
              <div className="text-left">
                <div className="text-xs font-extrabold text-slate-800 leading-tight">system admin</div>
                <span className="text-[9px] text-slate-400 font-semibold">{profile.email}</span>
              </div>
            </div>

            {/* Card 2: Logout Action (Red Theme) */}
            <button
              onClick={handleLogout}
              className="flex items-center justify-center gap-2 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white border border-red-200 hover:border-red-600 rounded-2xl px-4 py-3.5 shadow-sm text-xs font-bold transition-all duration-300"
            >
              <HiOutlinePower className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        {/* VIEW 1: DASHBOARD OVERVIEW */}
        {activeMenu === 'dashboard' && (
          <div className="space-y-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">System Dashboard</h1>
              <p className="text-sm text-slate-500 mt-1">Real-time mock operations analytics and user statistics overview.</p>
            </div>

            {/* Metrics cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Card 1: Total Enquiries */}
              <div
                onClick={() => setActiveMenu('enquiries')}
                className="bg-white rounded-2xl p-6 border border-slate-200/85 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group cursor-pointer"
              >
                <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-accent to-amber-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Total Enquiries</span>
                    <h3 className="text-3xl font-extrabold text-slate-900 leading-none">{totalEnquiries}</h3>
                  </div>
                  <div className="p-2.5 rounded-xl bg-accent/10 text-accent group-hover:scale-110 transition-transform duration-300">
                    <HiOutlineChatBubbleLeftRight className="w-5 h-5" />
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[10px]">
                  <span className="text-slate-400 font-medium">Breakdown:</span>
                  <div className="flex gap-2">
                    <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-bold">Sales: {enquiries.filter(e => e.type === 'sales').length}</span>
                    <span className="bg-secondary/10 text-secondary px-1.5 py-0.5 rounded font-bold">Rent: {enquiries.filter(e => e.type === 'rental').length}</span>
                    <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-bold">Other: {enquiries.filter(e => e.type !== 'sales' && e.type !== 'rental').length}</span>
                  </div>
                </div>
              </div>

              {/* Card 2: Total Generators */}
              <div
                onClick={() => setActiveMenu('products')}
                className="bg-white rounded-2xl p-6 border border-slate-200/85 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group cursor-pointer"
              >
                <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-primary to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Total Generators</span>
                    <h3 className="text-3xl font-extrabold text-slate-900 leading-none">{totalGenerators}</h3>
                  </div>
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary group-hover:scale-110 transition-transform duration-300">
                    <HiOutlineSquares2X2 className="w-5 h-5" />
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[9px] leading-tight">
                  <div className="flex flex-wrap gap-x-2 gap-y-1 w-full justify-between">
                    <span className="text-slate-500">Diesel: <strong className="text-slate-700 font-bold">{dieselCount}</strong></span>
                    <span className="text-slate-500">Silent: <strong className="text-slate-700 font-bold">{silentCount}</strong></span>
                    <span className="text-slate-500">Air Cooled: <strong className="text-slate-700 font-bold">{airCooledCount}</strong></span>
                  </div>
                </div>
              </div>

              {/* Card 3: Total Jobs */}
              <div
                onClick={() => setActiveMenu('careers')}
                className="bg-white rounded-2xl p-6 border border-slate-200/85 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group cursor-pointer"
              >
                <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Total Jobs</span>
                    <h3 className="text-3xl font-extrabold text-slate-900 leading-none">{totalJobApplications + totalJobsPosted}</h3>
                  </div>
                  <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 group-hover:scale-110 transition-transform duration-300">
                    <HiOutlineBriefcase className="w-5 h-5" />
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Jobs Posted: <strong className="text-slate-800 font-bold">{totalJobsPosted}</strong></span>
                  <span className="text-slate-500">Applications: <strong className="text-slate-800 font-bold">{totalJobApplications}</strong></span>
                </div>
              </div>

              {/* Card 4: Total Photos */}
              <div
                onClick={() => setActiveMenu('gallery')}
                className="bg-white rounded-2xl p-6 border border-slate-200/85 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group cursor-pointer"
              >
                <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-amber-500 to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Total Photos</span>
                    <h3 className="text-3xl font-extrabold text-slate-900 leading-none">{totalPhotos}</h3>
                  </div>
                  <div className="p-2.5 rounded-xl bg-amber-50 text-amber-600 group-hover:scale-110 transition-transform duration-300">
                    <HiOutlineSquares2X2 className="w-5 h-5" />
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Gallery Items: <strong className="text-slate-800 font-bold">{totalPhotos}</strong></span>
                </div>
              </div>
            </div>

            {/* Monthly Enquiries Graph */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/85 shadow-sm hover:shadow-md transition-all duration-300 relative">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider">Enquiries Volume Trend</h3>
                  <p className="text-xs text-slate-400 mt-1">Dynamic month-wise analysis of incoming user enquiries. Rolls over automatically at month-end.</p>
                </div>
                <div className="flex items-center gap-4 text-xs font-bold">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                    <span className="text-slate-600">Total Enquiries</span>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl py-1 px-3 text-slate-500">
                    Current Month: <strong className="text-primary font-bold">{enquiriesCountByMonth[enquiriesCountByMonth.length - 1].count}</strong>
                  </div>
                </div>
              </div>

              {/* Chart container */}
              <div className="relative h-[240px] w-full mt-4">
                <svg className="w-full h-full" viewBox="0 0 600 240" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="chart-area-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0F5A3A" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#0F5A3A" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Grid Lines & Y Labels */}
                  {gridLines}

                  {/* X Axis month labels */}
                  {points.map((p, idx) => (
                    <text
                      key={idx}
                      x={p.x}
                      y={225}
                      textAnchor="middle"
                      className="text-[10px] font-bold fill-slate-400"
                    >
                      {p.label}
                    </text>
                  ))}

                  {/* Area path */}
                  <path d={areaPath} fill="url(#chart-area-grad)" />

                  {/* Stroke path */}
                  <path d={linePath} fill="none" stroke="#0F5A3A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

                  {/* Data Node Indicators (glow and circle) */}
                  {points.map((p, idx) => (
                    <g key={idx}>
                      {hoveredPoint?.index === idx && (
                        <circle cx={p.x} cy={p.y} r="8" fill="#0F5A3A" opacity="0.3" className="animate-ping" />
                      )}
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r={hoveredPoint?.index === idx ? "6" : "4.5"}
                        fill={hoveredPoint?.index === idx ? "#F58A1F" : "#0F5A3A"}
                        stroke="#FFFFFF"
                        strokeWidth="2"
                        className="transition-all duration-200 cursor-pointer"
                      />
                      {/* Wider invisible mouse interaction circle */}
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r="25"
                        fill="transparent"
                        className="cursor-pointer"
                        onMouseEnter={() => setHoveredPoint(p)}
                        onMouseLeave={() => setHoveredPoint(null)}
                      />
                    </g>
                  ))}
                </svg>

                {/* Hover Tooltip overlay */}
                {hoveredPoint && (
                  <div
                    className="absolute bg-slate-900/95 text-white text-[10px] rounded-xl py-2 px-3 shadow-lg border border-slate-700/60 pointer-events-none transition-all duration-150 backdrop-blur-sm z-10"
                    style={{
                      left: `${(hoveredPoint.x / 600) * 100}%`,
                      top: `${(hoveredPoint.y / 240) * 100 - 16}%`,
                      transform: 'translate(-50%, -100%)',
                    }}
                  >
                    <div className="text-amber-400 font-extrabold uppercase text-[8px] tracking-wider mb-0.5">{hoveredPoint.label}</div>
                    <div className="font-bold">{hoveredPoint.count} {hoveredPoint.count === 1 ? 'Enquiry' : 'Enquiries'}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* VIEW 2: PRODUCT MANAGEMENT CRUD */}
        {activeMenu === 'products' && (
          <div className="space-y-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">Product Management</h1>
              <p className="text-sm text-slate-500 mt-1">Statically manage generator catalog items, add or edit technical specs.</p>
            </div>

            {activeSubMenu === 'manage-products' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center bg-white rounded-2xl px-6 py-4 border border-slate-200/80 shadow-sm">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Generator Sales Catalog</h3>
                  <button onClick={() => setActiveSubMenu('add-generator')} className="btn-green text-[10px] py-1.5 px-3 rounded-lg flex items-center gap-1.5 font-bold">
                    <HiOutlinePlus className="w-3.5 h-3.5" /> Add Generator
                  </button>
                </div>

                {/* Horizontal Category Toggle Pill Bar */}
                <div className="flex bg-slate-200/60 p-1.5 rounded-2xl w-fit border border-slate-200/80 shadow-inner">
                  {['Diesel Generators', 'Silent Generators', 'Air-cooled Generators'].map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${selectedCategory === cat
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {/* Filtered Category Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.filter(p => p.category === selectedCategory).length === 0 ? (
                    <div className="col-span-full text-center py-16 text-xs text-slate-400 bg-white border border-slate-200/80 rounded-2xl shadow-sm">
                      No products catalogued in this category.
                    </div>
                  ) : (
                    products.filter(p => p.category === selectedCategory).map(prod => (
                      <div key={prod.id} className="bg-white rounded-3xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col justify-between h-[490px] group">
                        <div>
                          {/* Product Image Frame */}
                          <div className="aspect-video w-full bg-slate-900 flex items-center justify-center border-b border-slate-100 overflow-hidden relative">
                            {prod.image ? (
                              <img
                                src={prod.image}
                                alt={prod.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">No Image Uploaded</div>
                            )}
                          </div>

                          <div className="p-6 space-y-3">
                            <div className="flex justify-between items-start gap-4">
                              <h4 className="text-sm font-extrabold text-slate-900 leading-snug line-clamp-2 group-hover:text-primary transition-colors">{prod.name}</h4>
                              <span className="bg-primary/10 text-primary border border-primary/10 px-2.5 py-0.5 rounded-full text-[9px] font-extrabold flex-shrink-0">
                                {prod.specs['Power Rating'] || 'N/A'}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{prod.desc}</p>
                            <div className="text-[10px] space-y-1 bg-slate-50 p-3 rounded-xl border border-slate-100/60">
                              <div className="text-slate-500">Voltage: <strong className="text-slate-700 font-bold">{prod.specs['Voltage'] || 'N/A'}</strong></div>
                              <div className="text-slate-500">Engine Make: <strong className="text-slate-700 font-bold">{prod.specs['Engine Make'] || 'N/A'}</strong></div>
                            </div>
                          </div>
                        </div>
                        <div className="p-6 pt-0">
                          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                            <button onClick={() => handleEditProductClick(prod)} className="flex items-center gap-1.5 py-2 px-3.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 text-xs font-bold border border-slate-200/60 transition-all">
                              <HiOutlinePencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button onClick={() => handleDeleteProduct(prod.id, prod.name)} className="flex items-center gap-1.5 py-2 px-3.5 rounded-xl bg-accent/5 hover:bg-accent/10 text-accent text-xs font-bold border border-accent/10 transition-all">
                              <HiOutlineTrash className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {activeSubMenu === 'add-generator' && (
              <div className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm max-w-4xl">
                <button
                  type="button"
                  onClick={() => setActiveSubMenu('manage-products')}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-6 pb-2 border-b border-slate-100">Add New Generator Product</h3>
                <form onSubmit={handleAddProduct} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Generator Name</label>
                      <input type="text" required value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="e.g. Cummins 250 kVA Generator" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400 transition-all" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Category</label>
                      <select value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all font-bold">
                        <option value="Diesel Generators">Diesel Generators</option>
                        <option value="Silent Generators">Silent Generators</option>
                        <option value="Air-cooled Generators">Air-cooled Generators</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Product Image (Max 800 KB)</label>
                    <div className="flex flex-col sm:flex-row gap-6 items-center bg-slate-50 p-5 rounded-2xl border border-slate-200/60">
                      <div className="relative overflow-hidden">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleProductImageUpload(e, false)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <button type="button" className="bg-white hover:bg-slate-100 text-slate-800 py-2.5 px-4 rounded-xl font-bold text-xs border border-slate-255 transition-colors shadow-sm">
                          Choose Photo
                        </button>
                      </div>
                      {newProduct.image ? (
                        <div className="relative w-28 h-20 bg-slate-900 border border-slate-200 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm">
                          <img
                            src={newProduct.image}
                            alt="Product preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">No File Chosen</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Short Description</label>
                    <textarea rows={3} required value={newProduct.desc} onChange={e => setNewProduct({ ...newProduct, desc: e.target.value })} placeholder="Enter brief technical description details..." className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 placeholder-slate-400 transition-all leading-relaxed" />
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-2 font-bold uppercase tracking-widest">Key Equipment Features</label>
                    <div className="space-y-3">
                      {(newProduct.features || []).map((feature, idx) => (
                        <div key={idx} className="flex gap-3 items-center">
                          <input
                            type="text"
                            required
                            value={feature}
                            onChange={e => handleNewProductFeatureChange(idx, e.target.value)}
                            placeholder={`Feature #${idx + 1} (e.g. High-performance air filter)`}
                            className="flex-grow bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400 transition-all"
                          />
                          {(newProduct.features || []).length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveNewProductFeatureField(idx)}
                              className="text-red-500 hover:text-red-700 font-bold px-2 text-sm transition-colors"
                              title="Remove Feature"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddNewProductFeatureField}
                      className="mt-3 text-xs font-bold text-primary hover:text-primary-dark bg-primary/10 hover:bg-primary/20 px-4 py-2 rounded-xl transition-all"
                    >
                      + Add Feature
                    </button>
                  </div>

                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary border-b border-slate-100 pb-2">Technical Specifications</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Power Rating</label>
                      <input type="text" required value={newProduct.specs['Power Rating'] || ''} onChange={e => setNewProduct({ ...newProduct, specs: { ...newProduct.specs, 'Power Rating': e.target.value } })} placeholder="e.g. 125 kVA / 100 kW" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Voltage</label>
                      <input type="text" required value={newProduct.specs['Voltage'] || ''} onChange={e => setNewProduct({ ...newProduct, specs: { ...newProduct.specs, 'Voltage': e.target.value } })} placeholder="e.g. 415V / Three Phase" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Engine Make</label>
                      <input type="text" required value={newProduct.specs['Engine Make'] || ''} onChange={e => setNewProduct({ ...newProduct, specs: { ...newProduct.specs, 'Engine Make': e.target.value } })} placeholder="e.g. Cummins India" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Acoustic Level</label>
                      <input type="text" required value={newProduct.specs['Acoustic Level'] || ''} onChange={e => setNewProduct({ ...newProduct, specs: { ...newProduct.specs, 'Acoustic Level': e.target.value } })} placeholder="e.g. < 75 dBA at 1 Meter" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Fuel Consumption</label>
                      <input type="text" required value={newProduct.specs['Fuel Consumption'] || ''} onChange={e => setNewProduct({ ...newProduct, specs: { ...newProduct.specs, 'Fuel Consumption': e.target.value } })} placeholder="e.g. 165 Liters/Hour at 75% load" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400" />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Save Product</button>
                    <button type="button" onClick={() => setActiveSubMenu('manage-products')} className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-6 rounded-xl font-bold text-xs transition-colors">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {activeSubMenu === 'edit-generator' && editingProduct && (
              <div className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm max-w-4xl">
                <button
                  type="button"
                  onClick={() => { setEditingProduct(null); setActiveSubMenu('manage-products'); }}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-6 pb-2 border-b border-slate-100">Edit Generator details: {editingProduct.name}</h3>
                <form onSubmit={handleSaveProductEdit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Generator Name</label>
                      <input type="text" required value={editingProduct.name} onChange={e => setEditingProduct({ ...editingProduct, name: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Category</label>
                      <select value={editingProduct.category} onChange={e => setEditingProduct({ ...editingProduct, category: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all font-bold">
                        <option value="Diesel Generators">Diesel Generators</option>
                        <option value="Silent Generators">Silent Generators</option>
                        <option value="Air-cooled Generators">Air-cooled Generators</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Product Image (Max 800 KB)</label>
                    <div className="flex flex-col sm:flex-row gap-6 items-center bg-slate-50 p-5 rounded-2xl border border-slate-200/60">
                      <div className="relative overflow-hidden">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleProductImageUpload(e, true)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <button type="button" className="bg-white hover:bg-slate-100 text-slate-800 py-2.5 px-4 rounded-xl font-bold text-xs border border-slate-250 transition-colors shadow-sm">
                          Choose Photo
                        </button>
                      </div>
                      {editingProduct.image ? (
                        <div className="relative w-28 h-20 bg-slate-900 border border-slate-200 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm">
                          <img
                            src={editingProduct.image}
                            alt="Product preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">No Image Uploaded</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Description</label>
                    <textarea rows={3} required value={editingProduct.desc} onChange={e => setEditingProduct({ ...editingProduct, desc: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 transition-all leading-relaxed" />
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-2 font-bold uppercase tracking-widest">Key Equipment Features</label>
                    <div className="space-y-3">
                      {(editingProduct.features || []).map((feature, idx) => (
                        <div key={idx} className="flex gap-3 items-center">
                          <input
                            type="text"
                            required
                            value={feature}
                            onChange={e => handleEditProductFeatureChange(idx, e.target.value)}
                            placeholder={`Feature #${idx + 1} (e.g. High-performance air filter)`}
                            className="flex-grow bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400 transition-all"
                          />
                          {(editingProduct.features || []).length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveEditProductFeatureField(idx)}
                              className="text-red-500 hover:text-red-700 font-bold px-2 text-sm transition-colors"
                              title="Remove Feature"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddEditProductFeatureField}
                      className="mt-3 text-xs font-bold text-primary hover:text-primary-dark bg-primary/10 hover:bg-primary/20 px-4 py-2 rounded-xl transition-all"
                    >
                      + Add Feature
                    </button>
                  </div>

                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary border-b border-slate-100 pb-2">Technical Specifications</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Power Rating</label>
                      <input type="text" required value={editingProduct.specs['Power Rating'] || ''} onChange={e => setEditingProduct({ ...editingProduct, specs: { ...editingProduct.specs, 'Power Rating': e.target.value } })} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Voltage</label>
                      <input type="text" required value={editingProduct.specs['Voltage'] || ''} onChange={e => setEditingProduct({ ...editingProduct, specs: { ...editingProduct.specs, 'Voltage': e.target.value } })} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Engine Make</label>
                      <input type="text" required value={editingProduct.specs['Engine Make'] || ''} onChange={e => setEditingProduct({ ...editingProduct, specs: { ...editingProduct.specs, 'Engine Make': e.target.value } })} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Acoustic Level</label>
                      <input type="text" required value={editingProduct.specs['Acoustic Level'] || ''} onChange={e => setEditingProduct({ ...editingProduct, specs: { ...editingProduct.specs, 'Acoustic Level': e.target.value } })} placeholder="e.g. < 75 dBA at 1 Meter" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1.5 font-bold uppercase tracking-widest">Fuel Consumption</label>
                      <input type="text" required value={editingProduct.specs['Fuel Consumption'] || ''} onChange={e => setEditingProduct({ ...editingProduct, specs: { ...editingProduct.specs, 'Fuel Consumption': e.target.value } })} placeholder="e.g. 165 Liters/Hour at 75% load" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900" />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Save Changes</button>
                    <button type="button" onClick={() => { setEditingProduct(null); setActiveSubMenu('manage-products'); }} className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-6 rounded-xl font-bold text-xs transition-colors">Cancel</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* VIEW 3: ENQUIRY MANAGEMENT */}
        {activeMenu === 'enquiries' && (() => {
          const countFor = (t) => enquiries.filter(e => e.type === t).length;
          return (
            <div className="space-y-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">Enquiry Management</h1>
                <p className="text-sm text-slate-500 mt-1">Review customer quotes, setup requests, and contact queries submitted from the website.</p>
              </div>

              {/* Total Enquiries Badges row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
                {[
                  { key: 'sales', label: 'Sales', count: countFor('sales'), border: 'border-l-primary' },
                  { key: 'rental', label: 'Rental', count: countFor('rental'), border: 'border-l-secondary' },
                  { key: 'maintenance', label: 'AMC Service', count: countFor('maintenance'), border: 'border-l-emerald-500' },
                  { key: 'solar', label: 'Solar Install', count: countFor('solar'), border: 'border-l-amber-500' },
                  { key: 'panels', label: 'LT Panels', count: countFor('panels'), border: 'border-l-cyan-500' },
                  { key: 'cutting-tools', label: 'Cutting Tools', count: countFor('cutting-tools'), border: 'border-l-rose-500' },
                  { key: 'telecom', label: 'Telecom NOC', count: countFor('telecom'), border: 'border-l-purple-500' },
                  { key: 'contact-us', label: 'General', count: countFor('contact-us'), border: 'border-l-slate-400' }
                ].map(item => (
                  <div
                    key={item.key}
                    onClick={() => setActiveEnquiryTab(item.key)}
                    className={`bg-white rounded-2xl p-4 border border-slate-200 shadow-sm border-l-4 ${item.border} cursor-pointer hover:shadow hover:-translate-y-0.5 transition-all ${activeEnquiryTab === item.key ? 'ring-2 ring-primary/20 bg-slate-50/50' : ''
                      }`}
                  >
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">{item.label}</span>
                    <span className="text-xl font-extrabold text-slate-900 mt-1 block">{item.count}</span>
                  </div>
                ))}
              </div>

              {/* Sub Tabs */}
              <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-4">
                {[
                  { key: 'sales', label: 'Generator Sales' },
                  { key: 'rental', label: 'Generator Rental' },
                  { key: 'maintenance', label: 'Maintenance & AMC' },
                  { key: 'solar', label: 'Solar Installation' },
                  { key: 'panels', label: 'Electrical Panels' },
                  { key: 'cutting-tools', label: 'Cutting Tools' },
                  { key: 'telecom', label: 'Telecom Services' },
                  { key: 'contact-us', label: 'Contact Us' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveEnquiryTab(tab.key)}
                    className={`px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border border-slate-200/40 ${activeEnquiryTab === tab.key ? 'bg-primary text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Enquiries Grid */}
              <div className="grid grid-cols-1 gap-6">
                {enquiries.filter(e => e.type === activeEnquiryTab).length === 0 ? (
                  <div className="p-16 text-center text-sm text-slate-400 bg-white rounded-3xl border border-slate-200/80 shadow-sm">
                    No enquiries logged in this category.
                  </div>
                ) : (
                  enquiries.filter(e => e.type === activeEnquiryTab).map(enq => (
                    <div key={enq.id} className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm flex flex-col lg:flex-row lg:items-start justify-between gap-6 hover:shadow-md transition-shadow">
                      <div className="space-y-4 flex-grow">
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="text-base font-extrabold text-slate-900">{enq.name}</h3>
                          <span className="text-xs text-slate-400 font-medium italic">{enq.date}</span>
                          <span className={`px-3 py-1 rounded-full text-[9px] font-extrabold uppercase tracking-widest ${enq.status === 'pending' ? 'bg-accent/10 text-accent border border-accent/20' :
                              enq.status === 'contacted' ? 'bg-primary/10 text-primary border border-primary/20' :
                                'bg-[#EEF2F5] text-[#5A7A5D] border border-slate-200'
                            }`}>
                            {enq.status}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-6 gap-y-1.5 font-semibold bg-slate-50 py-2 px-4 rounded-xl border border-slate-100 w-fit">
                          {enq.email && <span>Email: <strong className="text-slate-800">{enq.email}</strong></span>}
                          <span>Phone: <strong className="text-slate-800">{enq.phone}</strong></span>
                          {enq.city && <span>Location: <strong className="text-slate-800">{enq.city}</strong></span>}
                          {enq.productName && <span>Product: <strong className="text-emerald-700">{enq.productName}</strong></span>}
                          {enq.capacity && <span>Capacity: <strong className="text-amber-700">{enq.capacity}</strong></span>}
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed bg-[#F8FAFC] rounded-2xl p-5 border border-slate-100 italic">
                          "{enq.message}"
                        </p>
                      </div>

                      <div className="flex flex-wrap lg:flex-col gap-2.5 flex-shrink-0 lg:w-44">
                        <button onClick={() => handleUpdateEnquiryStatus(enq.id, 'contacted')} className="flex-grow lg:w-full flex items-center justify-center gap-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-xl py-3 px-4 text-xs font-bold transition-all">
                          <HiOutlineClock className="w-4 h-4" /> Mark Contacted
                        </button>
                        <button onClick={() => handleUpdateEnquiryStatus(enq.id, 'closed')} className="flex-grow lg:w-full flex items-center justify-center gap-1.5 bg-[#EEF2F5] hover:bg-slate-100 text-success-dark border border-slate-200 rounded-xl py-3 px-4 text-xs font-bold transition-all">
                          <HiOutlineCheckCircle className="w-4 h-4" /> Resolve/Close
                        </button>
                        <button onClick={() => handleDeleteEnquiry(enq.id, enq.name)} className="flex-grow lg:w-full flex items-center justify-center gap-1.5 bg-accent/5 hover:bg-accent/10 text-accent border border-accent/10 rounded-xl py-3 px-4 text-xs font-bold transition-all">
                          <HiOutlineTrash className="w-4 h-4" /> Delete Enquiry
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })()}

        {/* VIEW 4: CAREER MANAGEMENT */}
        {activeMenu === 'careers' && (
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">Career Center</h1>
                <p className="text-sm text-slate-500 mt-1">Manage public job openings, post new career options, and review incoming candidate applications.</p>
              </div>

              {/* Option toggle buttons */}
              <div className="flex bg-slate-200/60 p-1 rounded-xl border border-slate-200/80 shadow-inner">
                <button
                  type="button"
                  onClick={() => { setCareerToggle('applications'); setEditingJobOpening(null); setIsAddingJobOpening(false); }}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${careerToggle === 'applications' ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  Applications ({jobs.length})
                </button>
                <button
                  type="button"
                  onClick={() => { setCareerToggle('postings'); setEditingJobOpening(null); setIsAddingJobOpening(false); }}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${careerToggle === 'postings' ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  Manage Job Posts ({jobOpenings.length})
                </button>
              </div>
            </div>

            {/* Toggle Mode 1: Job Applications Feed */}
            {careerToggle === 'applications' && (
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Candidate Applications Feed</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider font-bold">
                        <th className="p-4">Applicant</th>
                        <th className="p-4">Position</th>
                        <th className="p-4">Experience</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 text-center">Resume PDF</th>
                        <th className="p-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {jobs.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-16 text-slate-400">
                            No job applications received yet.
                          </td>
                        </tr>
                      ) : (
                        jobs.map(job => (
                          <tr key={job.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="p-4">
                              <div className="font-bold text-slate-900">{job.name}</div>
                              <div className="text-[10px] text-slate-400">{job.phone} | {job.email}</div>
                              {job.letter && (
                                <div className="text-[10px] text-slate-500 italic mt-1 max-w-sm truncate" title={job.letter}>
                                  "{job.letter}"
                                </div>
                              )}
                            </td>
                            <td className="p-4 text-slate-800 font-bold">{job.position}</td>
                            <td className="p-4 text-accent font-bold">{job.experience}</td>
                            <td className="p-4">
                              <span className={`px-2.5 py-0.5 rounded-full text-[8px] font-extrabold uppercase ${job.status === 'pending' ? 'bg-slate-100 text-slate-600 border border-slate-200/60' :
                                  job.status === 'shortlisted' ? 'bg-success/15 text-success-dark border border-success/30' :
                                    'bg-accent/15 text-accent border border-accent/30'
                                }`}>
                                {job.status}
                              </span>
                            </td>
                            <td className="p-4 text-center">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => handleViewResume(job.name, job.resume)}
                                  className="p-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 transition-colors"
                                  title="View PDF Resume"
                                >
                                  <HiOutlineEye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDownloadResume(job.name, job.resume, job.resumeName)}
                                  className="p-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 transition-colors"
                                  title="Download PDF"
                                >
                                  <HiOutlineArrowDownTray className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => handleUpdateJobStatus(job.id, 'shortlisted')}
                                  className="btn-green text-[9px] py-1 px-2.5 rounded-lg font-bold hover:shadow-sm"
                                  disabled={job.status === 'shortlisted'}
                                >
                                  Shortlist
                                </button>
                                <button
                                  onClick={() => handleUpdateJobStatus(job.id, 'rejected')}
                                  className="bg-slate-100 hover:bg-slate-200 text-slate-800 text-[9px] py-1 px-2.5 rounded-lg font-bold border border-slate-200 transition-all"
                                  disabled={job.status === 'rejected'}
                                >
                                  Reject
                                </button>
                                <button
                                  onClick={() => handleDeleteJobApplication(job.id, job.name)}
                                  className="p-1 text-accent hover:bg-accent/10 border border-transparent hover:border-accent/20 rounded transition-all"
                                  title="Delete Application"
                                >
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Toggle Mode 2: Manage Job Postings */}
            {careerToggle === 'postings' && !isAddingJobOpening && !editingJobOpening && (
              <div className="space-y-6">
                <div className="flex justify-between items-center bg-white rounded-2xl px-6 py-4 border border-slate-200/80 shadow-sm">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Active Career Postings</h3>
                  <button
                    onClick={() => setIsAddingJobOpening(true)}
                    className="btn-green text-[10px] py-1.5 px-3 rounded-lg flex items-center gap-1.5 font-bold"
                  >
                    <HiOutlinePlus className="w-3.5 h-3.5" /> Add New Job Post
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {jobOpenings.length === 0 ? (
                    <div className="col-span-full text-center py-16 text-xs text-slate-400 bg-white border border-slate-200/80 rounded-2xl shadow-sm">
                      No career postings catalogued. Use the button above to add one.
                    </div>
                  ) : (
                    jobOpenings.map(op => (
                      <div key={op.id} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all duration-300 p-6 flex flex-col justify-between h-[360px] group relative">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start gap-4">
                            <span className="badge-gold text-[9px] uppercase tracking-wider">{op.dept}</span>
                            <span className="text-[10px] font-bold text-slate-400">{op.location}</span>
                          </div>

                          <h4 className="text-sm font-extrabold text-slate-900 group-hover:text-primary transition-colors leading-snug line-clamp-1">{op.title}</h4>
                          <p className="text-xs text-slate-500 line-clamp-3 leading-relaxed">{op.desc}</p>

                          {op.requirements && op.requirements.length > 0 && (
                            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                              <span className="text-[8px] font-extrabold text-slate-400 uppercase tracking-widest block mb-1.5">Key Criteria</span>
                              <ul className="space-y-1 text-[10px] text-slate-600 line-clamp-2">
                                {op.requirements.map((req, rIdx) => (
                                  <li key={rIdx} className="truncate">• {req}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 mt-4">
                          <button
                            onClick={() => handleEditJobOpeningClick(op)}
                            className="flex items-center gap-1.5 py-1.5 px-3 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 text-xs font-bold border border-slate-200/60 transition-all"
                          >
                            <HiOutlinePencil className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteJobOpening(op.id, op.title)}
                            className="flex items-center gap-1.5 py-1.5 px-3 rounded-lg bg-accent/5 hover:bg-accent/10 text-accent text-xs font-bold border border-accent/10 transition-all"
                          >
                            <HiOutlineTrash className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Sub-form: Add New Job Post */}
            {careerToggle === 'postings' && isAddingJobOpening && (
              <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm max-w-3xl">
                <button
                  type="button"
                  onClick={() => setIsAddingJobOpening(false)}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-6 border-b border-slate-100 pb-3">Post New Job Opening</h3>
                <form onSubmit={handleAddJobOpening} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Job Title</label>
                      <input
                        type="text"
                        required
                        value={newJobOpening.title}
                        onChange={e => setNewJobOpening(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g. Senior Service Technician"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Department / Division</label>
                      <input
                        type="text"
                        required
                        value={newJobOpening.dept}
                        onChange={e => setNewJobOpening(prev => ({ ...prev, dept: e.target.value }))}
                        placeholder="e.g. Telecom Operations"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Location</label>
                      <input
                        type="text"
                        required
                        value={newJobOpening.location}
                        onChange={e => setNewJobOpening(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="e.g. Pune MIDC"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Experience Required</label>
                      <input
                        type="text"
                        required
                        value={newJobOpening.experience}
                        onChange={e => setNewJobOpening(prev => ({ ...prev, experience: e.target.value }))}
                        placeholder="e.g. 3+ Years"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Job Description</label>
                    <textarea
                      rows={3}
                      required
                      value={newJobOpening.desc}
                      onChange={e => setNewJobOpening(prev => ({ ...prev, desc: e.target.value }))}
                      placeholder="Brief details about daily operations, reporting structures, or shift requirements..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 transition-all leading-relaxed"
                    />
                  </div>

                  {/* Requirements List Editor */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Key Requirements / Skills</span>
                      <button
                        type="button"
                        onClick={() => addRequirementInput(false)}
                        className="text-primary text-[10px] font-bold uppercase tracking-wide hover:underline"
                      >
                        + Add Requirement
                      </button>
                    </div>

                    <div className="space-y-3">
                      {newJobOpening.requirements.map((req, rIdx) => (
                        <div key={rIdx} className="flex gap-2 items-center">
                          <input
                            type="text"
                            required
                            value={req}
                            onChange={e => handleRequirementChange(rIdx, e.target.value, false)}
                            placeholder={`Requirement #${rIdx + 1}`}
                            className="flex-grow bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                          />
                          {newJobOpening.requirements.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRequirementInput(rIdx, false)}
                              className="p-2 text-accent hover:bg-accent/5 rounded-lg border border-transparent hover:border-accent/10 transition-colors"
                              title="Delete requirement line"
                            >
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Publish Job Opening</button>
                    <button
                      type="button"
                      onClick={() => setIsAddingJobOpening(false)}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-6 rounded-xl font-bold text-xs transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Sub-form: Edit Existing Job Post */}
            {careerToggle === 'postings' && editingJobOpening && (
              <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm max-w-3xl">
                <button
                  type="button"
                  onClick={() => setEditingJobOpening(null)}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-6 border-b border-slate-100 pb-3">Edit Job Post: {editingJobOpening.title}</h3>
                <form onSubmit={handleSaveJobOpeningEdit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Job Title</label>
                      <input
                        type="text"
                        required
                        value={editingJobOpening.title}
                        onChange={e => setEditingJobOpening(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Department / Division</label>
                      <input
                        type="text"
                        required
                        value={editingJobOpening.dept}
                        onChange={e => setEditingJobOpening(prev => ({ ...prev, dept: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Location</label>
                      <input
                        type="text"
                        required
                        value={editingJobOpening.location}
                        onChange={e => setEditingJobOpening(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Experience Required</label>
                      <input
                        type="text"
                        required
                        value={editingJobOpening.experience}
                        onChange={e => setEditingJobOpening(prev => ({ ...prev, experience: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Job Description</label>
                    <textarea
                      rows={3}
                      required
                      value={editingJobOpening.desc}
                      onChange={e => setEditingJobOpening(prev => ({ ...prev, desc: e.target.value }))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 leading-relaxed"
                    />
                  </div>

                  {/* Requirements List Editor */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Key Requirements / Skills</span>
                      <button
                        type="button"
                        onClick={() => addRequirementInput(true)}
                        className="text-primary text-[10px] font-bold uppercase tracking-wide hover:underline"
                      >
                        + Add Requirement
                      </button>
                    </div>

                    <div className="space-y-3">
                      {editingJobOpening.requirements.map((req, rIdx) => (
                        <div key={rIdx} className="flex gap-2 items-center">
                          <input
                            type="text"
                            required
                            value={req}
                            onChange={e => handleRequirementChange(rIdx, e.target.value, true)}
                            placeholder={`Requirement #${rIdx + 1}`}
                            className="flex-grow bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                          />
                          {editingJobOpening.requirements.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRequirementInput(rIdx, true)}
                              className="p-2 text-accent hover:bg-accent/5 rounded-lg border border-transparent hover:border-accent/10 transition-colors"
                              title="Delete requirement line"
                            >
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Save Changes</button>
                    <button
                      type="button"
                      onClick={() => setEditingJobOpening(null)}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-6 rounded-xl font-bold text-xs transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* VIEW 5: DIVISIONS STATIC TEMPLATES EDITOR */}
        {activeMenu === 'divisions' && (
          <div className="space-y-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">Divisions Management</h1>
              <p className="text-sm text-slate-500 mt-1">Configure categories and products dynamically for the user pages.</p>
            </div>

            {/* Division Sub-tabs */}
            <div className="flex gap-2 border-b border-slate-200 pb-4">
              {[
                { key: 'solar', label: 'Solar Solutions' },
                { key: 'generator', label: 'Generator Solutions' },
                { key: 'panels', label: 'Electrical Panels' },
                { key: 'cutting', label: 'Cutting Tools' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveDivisionTab(tab.key);
                    setIsAddingDivProduct(false);
                    setEditingDivProduct(null);
                    setEditingCategory(null);
                  }}
                  className={`px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border border-slate-200/40 ${activeDivisionTab === tab.key ? 'bg-accent text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Column: Category Management */}
              <div className="lg:col-span-4 space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm space-y-4">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">
                    {editingCategory ? 'Edit Category' : 'Create New Category'}
                  </h3>
                  <form onSubmit={editingCategory ? handleSaveDivisionCategoryEdit : handleAddDivisionCategory} className="space-y-4">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1 font-bold uppercase tracking-widest">Category Name</label>
                      <input
                        type="text"
                        required
                        value={editingCategory ? editingCategory.name : newCategory.name}
                        onChange={e => {
                          if (editingCategory) {
                            setEditingCategory({ ...editingCategory, name: e.target.value });
                          } else {
                            setNewCategory({ ...newCategory, name: e.target.value });
                          }
                        }}
                        placeholder="e.g. Residential Solar"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1 font-bold uppercase tracking-widest">Description</label>
                      <textarea
                        rows={2}
                        value={editingCategory ? editingCategory.desc : newCategory.desc}
                        onChange={e => {
                          if (editingCategory) {
                            setEditingCategory({ ...editingCategory, desc: e.target.value });
                          } else {
                            setNewCategory({ ...newCategory, desc: e.target.value });
                          }
                        }}
                        placeholder="Brief overview of this category..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" className="btn-green text-[10px] py-2 px-4 rounded-xl font-bold">
                        {editingCategory ? 'Save Changes' : 'Create Category'}
                      </button>
                      {editingCategory && (
                        <button
                          type="button"
                          onClick={() => setEditingCategory(null)}
                          className="bg-slate-100 text-slate-600 text-[10px] py-2 px-4 rounded-xl font-bold"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                </div>

                {/* Existing Categories List */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm space-y-3">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">
                    Existing Categories
                  </h3>
                  {divisionCategories.length === 0 ? (
                    <p className="text-xs text-slate-400">No categories created yet.</p>
                  ) : (
                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                      {divisionCategories.map(cat => (
                        <div key={cat.id} className="p-3 bg-slate-50 rounded-xl border border-slate-150 flex justify-between items-center gap-3">
                          <div className="overflow-hidden">
                            <span className="text-xs font-bold text-slate-800 block truncate">{cat.name}</span>
                            <span className="text-[10px] text-slate-400 block truncate">{cat.desc || 'No description'}</span>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => setEditingCategory({ ...cat })}
                              className="p-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100"
                              title="Edit Category"
                            >
                              <HiOutlinePencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteDivisionCategory(cat.id, cat.name)}
                              className="p-1.5 bg-accent/5 border border-accent/10 text-accent rounded-lg hover:bg-accent/10"
                              title="Delete Category"
                            >
                              <HiOutlineTrash className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Product Management */}
              <div className="lg:col-span-8 space-y-6">
                {isAddingDivProduct ? (
                  <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm space-y-6">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingDivProduct(false);
                        setEditingDivProduct(null);
                        setCustomSpecs([{ key: '', value: '' }]);
                      }}
                      className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                    >
                      <HiOutlineArrowLeft className="w-4 h-4" />
                      Go Back
                    </button>
                    <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3">
                      {editingDivProduct ? `Edit Product: ${editingDivProduct.name}` : 'Add New Product'}
                    </h3>
                    <form onSubmit={editingDivProduct ? handleSaveDivProductEdit : handleAddDivProduct} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Product Name</label>
                          <input
                            type="text"
                            required
                            value={editingDivProduct ? editingDivProduct.name : newDivProduct.name}
                            onChange={e => {
                              if (editingDivProduct) {
                                setEditingDivProduct({ ...editingDivProduct, name: e.target.value });
                              } else {
                                setNewDivProduct({ ...newDivProduct, name: e.target.value });
                              }
                            }}
                            placeholder="e.g. Annular Cutter Set"
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Category</label>
                          <select
                            value={editingDivProduct ? editingDivProduct.category : newDivProduct.category}
                            onChange={e => {
                              if (editingDivProduct) {
                                setEditingDivProduct({ ...editingDivProduct, category: e.target.value });
                              } else {
                                setNewDivProduct({ ...newDivProduct, category: e.target.value });
                              }
                            }}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 font-bold"
                          >
                            {divisionCategories.map(cat => (
                              <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Product Image (Max 800 KB)</label>
                        <div className="flex flex-col sm:flex-row gap-6 items-center bg-slate-50 p-5 rounded-2xl border border-slate-200/60">
                          <div className="relative overflow-hidden">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleDivProductImageUpload(e, !!editingDivProduct)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            />
                            <button type="button" className="bg-white hover:bg-slate-100 text-slate-800 py-2.5 px-4 rounded-xl font-bold text-xs border border-slate-250 transition-colors shadow-sm">
                              Choose Photo
                            </button>
                          </div>
                          {(editingDivProduct ? editingDivProduct.image : newDivProduct.image) ? (
                            <div className="relative w-28 h-20 bg-slate-900 border border-slate-200 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm">
                              <img
                                src={editingDivProduct ? editingDivProduct.image : newDivProduct.image}
                                alt="Product preview"
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">No File Chosen</div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Description</label>
                        <textarea
                          rows={3}
                          required
                          value={editingDivProduct ? editingDivProduct.desc : newDivProduct.desc}
                          onChange={e => {
                            if (editingDivProduct) {
                              setEditingDivProduct({ ...editingDivProduct, desc: e.target.value });
                            } else {
                              setNewDivProduct({ ...newDivProduct, desc: e.target.value });
                            }
                          }}
                          placeholder="Technical details, descriptions..."
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900"
                        />
                      </div>

                      {/* Technical Specifications (Dynamic Editor) */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Technical Specifications</span>
                          <button
                            type="button"
                            onClick={addSpecField}
                            className="text-primary text-[10px] font-bold uppercase tracking-wide hover:underline"
                          >
                            + Add Spec Property
                          </button>
                        </div>
                        <div className="space-y-3">
                          {customSpecs.map((spec, sIdx) => (
                            <div key={sIdx} className="flex gap-4 items-center">
                              <input
                                type="text"
                                value={spec.key}
                                onChange={e => handleSpecChange(sIdx, 'key', e.target.value)}
                                placeholder="Property Name (e.g. System Capacity)"
                                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                              />
                              <input
                                type="text"
                                value={spec.value}
                                onChange={e => handleSpecChange(sIdx, 'value', e.target.value)}
                                placeholder="Property Value (e.g. 5 kW)"
                                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs outline-none focus:border-primary focus:bg-white text-slate-900"
                              />
                              {customSpecs.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeSpecField(sIdx)}
                                  className="p-2 text-accent hover:bg-accent/5 rounded-lg border border-transparent hover:border-accent/10 transition-colors"
                                >
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-4 pt-4 border-t border-slate-100">
                        <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">
                          {editingDivProduct ? 'Save Changes' : 'Save Product'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingDivProduct(false);
                            setEditingDivProduct(null);
                            setCustomSpecs([{ key: '', value: '' }]);
                          }}
                          className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-6 rounded-xl font-bold text-xs transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center bg-white rounded-2xl px-6 py-4 border border-slate-200/80 shadow-sm">
                      <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Product Range List</h3>
                      <button
                        onClick={() => {
                          setNewDivProduct({
                            name: '',
                            category: divisionCategories[0]?.name || '',
                            image: '',
                            desc: '',
                            specs: {}
                          });
                          if (activeDivisionTab === 'solar') {
                            setCustomSpecs([
                              { key: 'System Capacity', value: '' },
                              { key: 'Panel Type', value: '' },
                              { key: 'Inverter', value: '' },
                              { key: 'Warranty', value: '' }
                            ]);
                          } else if (activeDivisionTab === 'generator') {
                            setCustomSpecs([
                              { key: 'Rating', value: '' },
                              { key: 'Voltage', value: '' },
                              { key: 'Engine Make', value: '' },
                              { key: 'Fuel Consumption', value: '' }
                            ]);
                          } else {
                            setCustomSpecs([{ key: '', value: '' }]);
                          }
                          setIsAddingDivProduct(true);
                        }}
                        className="btn-green text-[10px] py-1.5 px-3 rounded-lg flex items-center gap-1.5 font-bold shadow-sm"
                      >
                        <HiOutlinePlus className="w-3.5 h-3.5" /> Add Product
                      </button>
                    </div>

                    {/* Products Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {divisionProducts.length === 0 ? (
                        <div className="col-span-full text-center py-16 text-xs text-slate-400 bg-white border border-slate-200/80 rounded-2xl shadow-sm">
                          No products found for this division. Add a product above!
                        </div>
                      ) : (
                        divisionProducts.map(prod => (
                          <div key={prod.id} className="bg-white rounded-3xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col justify-between h-[495px] group">
                            <div>
                              <div className="aspect-video w-full bg-slate-900 flex items-center justify-center border-b border-slate-100 overflow-hidden relative">
                                {prod.image ? (
                                  <img
                                    src={prod.image}
                                    alt={prod.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="text-xs text-slate-450 font-bold uppercase tracking-wider">No Photo Uploaded</div>
                                )}
                              </div>
                              <div className="p-6 space-y-3">
                                <div className="flex justify-between items-start gap-4">
                                  <h4 className="text-sm font-extrabold text-slate-900 leading-snug line-clamp-2 group-hover:text-primary transition-colors">{prod.name}</h4>
                                  <span className="bg-primary/10 text-primary border border-primary/10 px-2.5 py-0.5 rounded-full text-[9px] font-extrabold flex-shrink-0">
                                    {prod.category}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{prod.desc}</p>
                                {Object.keys(prod.specs || {}).length > 0 && (
                                  <div className="text-[10px] space-y-1 bg-slate-50 p-3 rounded-xl border border-slate-100/60 max-h-[85px] overflow-y-auto">
                                    {Object.entries(prod.specs).slice(0, 3).map(([k, v]) => (
                                      <div key={k} className="text-slate-500 truncate">
                                        {k}: <strong className="text-slate-700 font-bold">{v}</strong>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="p-6 pt-0">
                              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                                <button onClick={() => handleEditDivProductClick(prod)} className="flex items-center gap-1.5 py-2 px-3.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 text-xs font-bold border border-slate-200/60 transition-all">
                                  <HiOutlinePencil className="w-3.5 h-3.5" /> Edit
                                </button>
                                <button onClick={() => handleDeleteDivProduct(prod.id, prod.name)} className="flex items-center gap-1.5 py-2 px-3.5 rounded-xl bg-accent/5 hover:bg-accent/10 text-accent text-xs font-bold border border-accent/10 transition-all">
                                  <HiOutlineTrash className="w-3.5 h-3.5" /> Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* VIEW: MANAGE MEMBERS */}
        {activeMenu === 'members' && (
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">Manage Team & Board Members</h1>
                <p className="text-sm text-slate-500 mt-1">Configure details for Ashok Ravsaheb Joshi (Director) and other key leadership staff.</p>
              </div>
            </div>

            {/* If adding a member or editing one */}
            {!editingMember && !isAddingMember && (
              <div className="space-y-6">
                <div className="flex justify-between items-center bg-white rounded-2xl px-6 py-4 border border-slate-200/80 shadow-sm">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Board & Leadership List</h3>
                  <button onClick={() => {
                    setNewMember({ name: '', role: '', tagline: '', image: '', bio: '', quote: '', type: 'leadership' });
                    setIsAddingMember(true);
                  }} className="btn-green text-[10px] py-1.5 px-3.5 rounded-lg flex items-center gap-1.5 font-bold shadow-sm">
                    <HiOutlinePlus className="w-3.5 h-3.5" /> Add Member
                  </button>
                </div>

                {/* Team Members Listing */}
                {/* 1. Directors Row (First Single Row) */}
                {teamMembers.filter(m => m.type === 'director').length > 0 && (
                  <div className="space-y-3">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Directors</span>
                    <div className="grid grid-cols-1 gap-6">
                      {teamMembers.filter(m => m.type === 'director').map(member => (
                        <div key={member.id} className="bg-white rounded-3xl border border-slate-200/80 shadow-sm p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-md transition-shadow gap-6 h-auto">
                          <div className="flex flex-col sm:flex-row gap-5 items-start sm:items-center">
                            <img
                              src={member.image || DEFAULT_DIRECTOR_AVATAR}
                              alt={member.name}
                              className="w-24 h-24 rounded-2xl object-cover border border-slate-200 bg-slate-50 shadow-sm flex-shrink-0"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DEFAULT_DIRECTOR_AVATAR;
                              }}
                            />
                            <div className="overflow-hidden space-y-2">
                              <h4 className="text-base font-extrabold text-slate-900 leading-snug">{member.name}</h4>
                              <span className="text-xs text-accent font-bold uppercase tracking-wider block">{member.role}</span>
                              {member.bio && (
                                <p className="text-xs text-slate-500 leading-relaxed font-medium">
                                  {member.bio}
                                </p>
                              )}
                              {member.quote && (
                                <div className="text-xs text-slate-600 italic bg-slate-50 border-l-4 border-accent p-2.5 rounded-r-xl leading-relaxed max-w-2xl">
                                  "{member.quote}"
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-2 self-end sm:self-center flex-shrink-0">
                            <button onClick={() => handleEditMemberClick(member)} className="flex items-center gap-1.5 py-1.5 px-3.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 text-xs font-bold border border-slate-200/60 transition-all">
                              <HiOutlinePencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button onClick={() => handleDeleteMember(member.id, member.name)} className="flex items-center gap-1.5 py-1.5 px-3.5 rounded-xl bg-accent/5 hover:bg-accent/10 text-accent text-xs font-bold border border-accent/10 transition-all">
                              <HiOutlineTrash className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2. Other Key Leadership Members Row (3 Cards per row) */}
                {teamMembers.filter(m => m.type !== 'director').length > 0 && (
                  <div className="space-y-3 pt-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Key Leadership Staff</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {teamMembers.filter(m => m.type !== 'director').map(member => (
                        <div key={member.id} className="bg-white rounded-3xl border border-slate-200/80 shadow-sm p-6 flex flex-col justify-between hover:shadow-md transition-shadow h-auto relative">
                          {/* Reordering Controls */}
                          <div className="absolute top-4 right-4 flex flex-col gap-1.5 z-10">
                            <button
                              type="button"
                              onClick={() => handleMoveMember(member.id, 'up')}
                              className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-sm"
                              title="Move Up"
                            >
                              <FaArrowUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveMember(member.id, 'down')}
                              className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-sm"
                              title="Move Down"
                            >
                              <FaArrowDown className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="space-y-4">
                            <div className="flex gap-5 items-center">
                              <img
                                src={member.image || DEFAULT_LEADER_AVATAR}
                                alt={member.name}
                                className="w-20 h-20 rounded-2xl object-cover border border-slate-200 bg-slate-50 shadow-sm flex-shrink-0"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = DEFAULT_LEADER_AVATAR;
                                }}
                              />
                              <div className="overflow-hidden">
                                <h4 className="text-sm font-extrabold text-slate-900 leading-snug">{member.name}</h4>
                                <span className="text-xs text-accent font-bold uppercase tracking-wider block mt-1">{member.role}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-6">
                            <button onClick={() => handleEditMemberClick(member)} className="flex items-center gap-1.5 py-1.5 px-3.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 text-xs font-bold border border-slate-200/60 transition-all">
                              <HiOutlinePencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button onClick={() => handleDeleteMember(member.id, member.name)} className="flex items-center gap-1.5 py-1.5 px-3.5 rounded-xl bg-accent/5 hover:bg-accent/10 text-accent text-xs font-bold border border-accent/10 transition-all">
                              <HiOutlineTrash className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ADD MEMBER VIEW */}
            {isAddingMember && (
              <div className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm max-w-4xl">
                <button
                  type="button"
                  onClick={() => setIsAddingMember(false)}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <div className="mb-6 pb-2 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Add New Board / Leadership Member</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">All fields marked with <span className="text-rose-500 font-bold">*</span> are mandatory to fill.</p>
                  </div>
                  <span className="text-[10px] font-extrabold bg-rose-50 text-rose-600 border border-rose-200/60 px-2.5 py-1 rounded-full w-fit">
                    * Photo & Info Required
                  </span>
                </div>
                <form onSubmit={handleAddMember} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Full Name <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <input type="text" required value={newMember.name} onChange={e => setNewMember(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Ashok Ravsaheb Joshi" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400 transition-all" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Designation / Role <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <input type="text" required value={newMember.role} onChange={e => setNewMember(prev => ({ ...prev, role: e.target.value }))} placeholder="e.g. Chief Technical Officer (CTO)" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400 transition-all" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Member Type <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <select value={newMember.type} onChange={e => setNewMember(prev => ({ ...prev, type: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all font-bold">
                        <option value="leadership">Key Leadership Member</option>
                        <option value="director">Director</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Photo (File Upload) <span className="text-rose-500 font-extrabold">* (Mandatory)</span>
                      </label>
                      <div className={`flex flex-col sm:flex-row gap-6 items-center p-5 rounded-2xl border transition-all ${!newMember.image ? 'bg-rose-50/30 border-rose-200' : 'bg-slate-50 border-slate-200/60'}`}>
                        <div className="relative overflow-hidden">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={e => {
                              const target = e.target;
                              handleMemberPhotoUpload(e, (dataUrl) => {
                                setNewMember(prev => ({ ...prev, image: dataUrl }));
                                target.value = '';
                              });
                            }}
                            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
                          />
                          <button
                            type="button"
                            className={`py-2.5 px-4 rounded-xl font-bold text-xs border transition-all shadow-sm flex items-center gap-2 ${!newMember.image ? 'bg-rose-600 hover:bg-rose-700 text-white border-rose-600' : 'bg-white hover:bg-slate-100 text-slate-800 border-slate-250'}`}
                          >
                            <HiOutlinePlus className="w-4 h-4" /> {newMember.image ? 'Change Photo' : 'Upload Photo *'}
                          </button>
                        </div>
                        {newMember.image ? (
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-slate-950 border-2 border-emerald-500 flex items-center justify-center shadow-sm">
                              <img src={newMember.image} alt="Member preview" className="max-w-full max-h-full object-contain" />
                            </div>
                            <div className="text-left">
                              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">✓ Photo Loaded</span>
                              <span className="text-[10px] text-slate-400">Ready to save</span>
                            </div>
                          </div>
                        ) : (
                          <div className="border border-dashed border-rose-300 rounded-xl px-4 py-3 bg-white flex flex-col items-center sm:items-start justify-center">
                            <span className="text-xs text-rose-600 font-extrabold flex items-center gap-1">
                              ⚠ Photo is Required
                            </span>
                            <span className="text-[10px] text-slate-400">Please choose a photo file to continue</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {newMember.type === 'director' && (
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Description / Bio (For Directors) <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <textarea rows={4} required value={newMember.bio} onChange={e => setNewMember(prev => ({ ...prev, bio: e.target.value }))} placeholder="Ashok Ravsaheb Joshi has spent over 25 years..." className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 placeholder-slate-400 transition-all leading-relaxed text-sm" />
                    </div>
                  )}

                  {newMember.type === 'director' && (
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Quote (For Directors) <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <textarea rows={2} required value={newMember.quote} onChange={e => setNewMember(prev => ({ ...prev, quote: e.target.value }))} placeholder="Enter corporate leadership quote statement..." className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 placeholder-slate-400 transition-all leading-relaxed text-sm" />
                    </div>
                  )}

                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Save Member</button>
                    <button type="button" onClick={() => setIsAddingMember(false)} className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-6 rounded-xl font-bold text-xs transition-colors">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {/* EDIT MEMBER VIEW */}
            {editingMember && (
              <div className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm max-w-4xl">
                <button
                  type="button"
                  onClick={() => setEditingMember(null)}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <div className="mb-6 pb-2 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Edit Member details: {editingMember.name}</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">All fields marked with <span className="text-rose-500 font-bold">*</span> are mandatory to submit.</p>
                  </div>
                </div>
                <form onSubmit={handleSaveMemberEdit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Full Name <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <input type="text" required value={editingMember.name} onChange={e => setEditingMember(prev => ({ ...prev, name: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Designation / Role <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <input type="text" required value={editingMember.role} onChange={e => setEditingMember(prev => ({ ...prev, role: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Member Type <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <select value={editingMember.type} onChange={e => setEditingMember(prev => ({ ...prev, type: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all font-bold">
                        <option value="leadership">Key Leadership Member</option>
                        <option value="director">Director</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Photo (File Upload) <span className="text-rose-500 font-extrabold">* (Mandatory)</span>
                      </label>
                      <div className="flex flex-col sm:flex-row gap-6 items-center bg-slate-50 p-5 rounded-2xl border border-slate-200/60">
                        <div className="relative overflow-hidden">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={e => {
                              const target = e.target;
                              handleMemberPhotoUpload(e, (dataUrl) => {
                                setEditingMember(prev => ({ ...prev, image: dataUrl }));
                                target.value = '';
                              });
                            }}
                            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
                          />
                          <button type="button" className="bg-white hover:bg-slate-100 text-slate-800 py-2.5 px-4 rounded-xl font-bold text-xs border border-slate-250 transition-colors shadow-sm flex items-center gap-2">
                            <HiOutlinePlus className="w-4 h-4" /> Change Photo
                          </button>
                        </div>
                        {editingMember.image ? (
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-slate-950 border-2 border-emerald-500 flex items-center justify-center shadow-sm">
                              <img
                                src={editingMember.image}
                                alt="Member preview"
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = editingMember.type === 'director' ? DEFAULT_DIRECTOR_AVATAR : DEFAULT_LEADER_AVATAR;
                                }}
                              />
                            </div>
                            <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">✓ Photo Selected</span>
                          </div>
                        ) : (
                          <div className="border border-dashed border-rose-300 rounded-xl px-4 py-3 bg-white flex flex-col items-center sm:items-start justify-center">
                            <span className="text-xs text-rose-600 font-extrabold flex items-center gap-1">⚠ Photo is Required</span>
                            <span className="text-[10px] text-slate-400">Please choose a photo file</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {editingMember.type === 'director' && (
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Description / Bio (For Directors) <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <textarea rows={4} required value={editingMember.bio || ''} onChange={e => setEditingMember(prev => ({ ...prev, bio: e.target.value }))} placeholder="Ashok Ravsaheb Joshi has spent over 25 years..." className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 transition-all leading-relaxed text-sm" />
                    </div>
                  )}

                  {editingMember.type === 'director' && (
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">
                        Quote (For Directors) <span className="text-rose-500 font-extrabold">*</span>
                      </label>
                      <textarea rows={2} required value={editingMember.quote || ''} onChange={e => setEditingMember(prev => ({ ...prev, quote: e.target.value }))} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 transition-all leading-relaxed text-sm" />
                    </div>
                  )}

                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Save Changes</button>
                    <button type="button" onClick={() => setEditingMember(null)} className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-6 rounded-xl font-bold text-xs transition-colors">Cancel</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* VIEW 6: USER PROFILE SETTINGS */}
        {activeMenu === 'profile' && (
          <div className="space-y-8 max-w-4xl">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">Profile & Security</h1>
              <p className="text-sm text-slate-500 mt-1">Manage user information and sign-in passwords.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Profile Details */}
              <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-6 border-b border-slate-100 pb-3">Profile Settings</h3>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Full Name</label>
                    <input type="text" required value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Registered Email</label>
                    <input type="email" required disabled value={profile.email} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none opacity-60 cursor-not-allowed text-slate-900" />
                  </div>
                  <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Save Settings</button>
                </form>
              </div>

              {/* Password Settings */}
              <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-6 border-b border-slate-100 pb-3">Security & Password</h3>
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Current Password</label>
                    <input type="password" required value={passwords.current} onChange={e => setPasswords({ ...passwords, current: e.target.value })} placeholder="••••••••" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">New Password</label>
                    <input type="password" required value={passwords.new} onChange={e => setPasswords({ ...passwords, new: e.target.value })} placeholder="••••••••" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Confirm Password</label>
                    <input type="password" required value={passwords.confirm} onChange={e => setPasswords({ ...passwords, confirm: e.target.value })} placeholder="••••••••" className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900" />
                  </div>
                  <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Update Password</button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* VIEW: GROWTH STATISTICS */}
        {activeMenu === 'stats' && (
          <div className="space-y-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">Manage Growth Statistics</h1>
              <p className="text-sm text-slate-500 mt-1">Configure the four key metrics displayed in the "Green Gusto in Numbers" section on the home page.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm space-y-6">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3">Growth Stats Editor</h3>

              <form onSubmit={handleSaveGrowthStats} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {growthStats.map((stat, index) => (
                    <div key={stat.id || index} className="p-6 bg-slate-50 border border-slate-200 rounded-2xl space-y-4">
                      <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                        <span className="text-[10px] text-accent font-extrabold uppercase tracking-widest">Card {index + 1}</span>
                        <span className="text-xs font-bold text-slate-700">{stat.label || `Metric ${index + 1}`}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Number Value</label>
                          <input
                            type="number"
                            required
                            value={stat.num}
                            onChange={e => handleUpdateStatField(index, 'num', Number(e.target.value))}
                            className="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3 text-xs outline-none focus:border-primary text-slate-900 font-bold"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Suffix</label>
                          <input
                            type="text"
                            required
                            value={stat.suffix}
                            onChange={e => handleUpdateStatField(index, 'suffix', e.target.value)}
                            placeholder="e.g. +"
                            className="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3 text-xs outline-none focus:border-primary text-slate-900"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Card Title / Label</label>
                        <input
                          type="text"
                          required
                          value={stat.label}
                          onChange={e => handleUpdateStatField(index, 'label', e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3 text-xs outline-none focus:border-primary text-slate-900"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Card Subtitle / Description</label>
                        <input
                          type="text"
                          required
                          value={stat.sub}
                          onChange={e => handleUpdateStatField(index, 'sub', e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3 text-xs outline-none focus:border-primary text-slate-900"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Icon Indicator</label>
                        <select
                          value={stat.icon}
                          onChange={e => handleUpdateStatField(index, 'icon', e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3 text-xs outline-none focus:border-primary text-slate-900 font-bold"
                        >
                          <option value="Clock">Clock (Time)</option>
                          <option value="Wrench">Wrench (Tools)</option>
                          <option value="Clipboard">Clipboard (Checks)</option>
                          <option value="Building">Building (Clients)</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-4 pt-4 border-t border-slate-100">
                  <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">Save Stats Layout</button>
                  <button type="button" onClick={() => setActiveMenu('dashboard')} className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 px-6 rounded-xl font-bold text-xs transition-colors">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* VIEW 7: MANAGE GALLERY */}
        {activeMenu === 'gallery' && (
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-display font-extrabold text-slate-900">Gallery Portfolio Management</h1>
                <p className="text-sm text-slate-500 mt-1">Add new site project pictures, specify division classifications, and manage public portfolio items.</p>
              </div>

              <div className="flex bg-slate-200/60 p-1 rounded-xl border border-slate-200/80 shadow-inner">
                <button
                  type="button"
                  onClick={() => { setGalleryToggle('images'); setEditingGalleryItem(null); setIsAddingGallery(false); }}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${galleryToggle === 'images' ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  Gallery Images ({galleryItems.length})
                </button>
                <button
                  type="button"
                  onClick={() => { setGalleryToggle('categories'); setEditingGalleryCat(null); setIsAddingGalleryCat(false); }}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${galleryToggle === 'categories' ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  Manage Categories ({galleryCategories.length})
                </button>
              </div>
            </div>

            {/* CATEGORIES TAB SECTION */}
            {galleryToggle === 'categories' && !isAddingGalleryCat && !editingGalleryCat && (
              <div className="space-y-6">
                <div className="flex justify-between items-center bg-white rounded-2xl px-6 py-4 border border-slate-200/80 shadow-sm">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Active Gallery Categories</h3>
                  <button
                    onClick={() => {
                      setGalleryCatForm({ name: '', slug: '' });
                      setIsAddingGalleryCat(true);
                    }}
                    className="btn-green text-[10px] py-1.5 px-3 rounded-lg flex items-center gap-1.5 font-bold"
                  >
                    <HiOutlinePlus className="w-3.5 h-3.5" /> Add New Category
                  </button>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider font-bold">
                        <th className="p-4">Category Name</th>
                        <th className="p-4">Slug / Filter Key</th>
                        <th className="p-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {galleryCategories.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="text-center py-12 text-slate-400">
                            No gallery categories registered.
                          </td>
                        </tr>
                      ) : (
                        galleryCategories.map(cat => (
                          <tr key={cat.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="p-4 font-bold text-slate-800">{cat.name}</td>
                            <td className="p-4 font-mono text-slate-500">{cat.slug}</td>
                            <td className="p-4 text-center">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditingGalleryCat(cat);
                                    setGalleryCatForm({ name: cat.name, slug: cat.slug });
                                    setIsAddingGalleryCat(true);
                                  }}
                                  className="flex items-center gap-1.5 py-1 px-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200 transition-all font-bold text-[10px]"
                                >
                                  <HiOutlinePencil className="w-3 h-3" /> Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteGalleryCategory(cat.id, cat.name)}
                                  className="flex items-center gap-1.5 py-1 px-2.5 rounded-lg bg-accent/5 hover:bg-accent/10 text-accent border border-accent/10 transition-all font-bold text-[10px]"
                                >
                                  <HiOutlineTrash className="w-3 h-3" /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {galleryToggle === 'categories' && isAddingGalleryCat && (
              <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm max-w-xl">
                <button
                  type="button"
                  onClick={() => { setIsAddingGalleryCat(false); setEditingGalleryCat(null); }}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-6 border-b border-slate-100 pb-3">
                  {editingGalleryCat ? `Edit Category: ${editingGalleryCat.name}` : 'Add Gallery Category'}
                </h3>
                <form onSubmit={handleSaveGalleryCategory} className="space-y-6">
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Category Name</label>
                    <input
                      type="text"
                      required
                      value={galleryCatForm.name}
                      onChange={e => setGalleryCatForm({ ...galleryCatForm, name: e.target.value })}
                      placeholder="e.g. Industrial Generators"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Slug (Optional)</label>
                    <input
                      type="text"
                      value={galleryCatForm.slug}
                      onChange={e => setGalleryCatForm({ ...galleryCatForm, slug: e.target.value })}
                      placeholder="e.g. industrial-generators (Auto-generated if blank)"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all"
                    />
                  </div>
                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button type="submit" className="btn-green py-3 px-6 rounded-xl font-bold text-xs">
                      {editingGalleryCat ? 'Save Changes' : 'Create Category'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsAddingGalleryCat(false); setEditingGalleryCat(null); }}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-705 py-3 px-6 rounded-xl font-bold text-xs transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* IMAGES TAB SECTION */}
            {galleryToggle === 'images' && !isAddingGallery && !editingGalleryItem && (
              <div className="space-y-6">
                <div className="flex justify-between items-center bg-white rounded-2xl px-6 py-4 border border-slate-200/80 shadow-sm">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Active Gallery Portfolio ({galleryItems.length} items)</h3>
                  <button
                    onClick={() => {
                      setNewGalleryItem({ title: '', desc: '', category: galleryCategories[0]?.slug || '', image: '' });
                      setIsAddingGallery(true);
                    }}
                    className="btn-green py-3 px-5 rounded-xl flex items-center gap-2 font-bold shadow-md shadow-primary/10 transition-all text-xs"
                  >
                    <HiOutlinePlus className="w-4 h-4" /> Add New Image
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {galleryItems.length === 0 ? (
                    <div className="col-span-full text-center py-16 text-xs text-slate-400 bg-white border border-slate-200/80 rounded-2xl shadow-sm">
                      No gallery photos uploaded yet. Click the "+ Add New Image" button to start.
                    </div>
                  ) : (
                    galleryItems.map(item => (
                      <div key={item.id} className="bg-white rounded-2xl border border-slate-200/85 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col justify-between h-[390px] group relative">
                        <div>
                          <div className="aspect-video w-full bg-slate-955 flex items-center justify-center border-b border-slate-100 overflow-hidden relative">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="text-[10px] text-slate-400 font-bold uppercase">No Image</div>
                            )}
                            <span className="absolute top-3 left-3 bg-slate-900/85 backdrop-blur-sm text-amber-400 text-[8px] font-extrabold px-2.5 py-0.5 rounded-md uppercase tracking-wider border border-white/10">
                              {galleryCategories.find(c => c.slug === item.category)?.name || item.category}
                            </span>
                          </div>

                          <div className="p-5 space-y-2">
                            <h4 className="text-xs font-bold text-slate-900 line-clamp-2 uppercase tracking-wide leading-tight group-hover:text-primary transition-colors">
                              {item.title}
                            </h4>
                            <p className="text-[11px] text-slate-500 line-clamp-3 leading-relaxed">
                              {item.desc}
                            </p>
                          </div>
                        </div>

                        <div className="p-5 pt-0 border-t border-slate-50 flex gap-2">
                          <button
                            onClick={() => handleEditGalleryItemClick(item)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 text-xs font-bold border border-slate-200/60 transition-all"
                          >
                            <HiOutlinePencil className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteGalleryItem(item.id, item.title)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-accent/5 hover:bg-accent/10 text-accent text-xs font-bold border border-accent/10 transition-all"
                          >
                            <HiOutlineTrash className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* FULL PAGE ADD GALLERY ITEM FORM */}
            {galleryToggle === 'images' && isAddingGallery && (
              <div className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm max-w-4xl">
                <button
                  type="button"
                  onClick={() => setIsAddingGallery(false)}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-6 pb-2 border-b border-slate-100">Add New Portfolio Image</h3>
                <form onSubmit={handleAddGalleryItem} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Image Title</label>
                      <input
                        type="text"
                        required
                        value={newGalleryItem.title}
                        onChange={e => setNewGalleryItem(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g. Eicher 15kVA Shop Installation"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3.5 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 placeholder-slate-400 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Category / Division</label>
                      <select
                        value={newGalleryItem.category}
                        onChange={e => setNewGalleryItem(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3.5 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all font-bold"
                      >
                        {galleryCategories.map(cat => (
                          <option key={cat.id} value={cat.slug}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Description</label>
                    <textarea
                      rows={3}
                      required
                      value={newGalleryItem.desc}
                      onChange={e => setNewGalleryItem(prev => ({ ...prev, desc: e.target.value }))}
                      placeholder="Provide details about the installation setup, location, or equipment specs..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3.5 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 placeholder-slate-400 transition-all leading-relaxed"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Upload Photo (Max 800 KB)</label>
                    <div className="flex flex-col sm:flex-row gap-6 items-center bg-slate-50 p-5 rounded-2xl border border-slate-200/60">
                      <div className="relative overflow-hidden">
                        <input
                          id="gallery-file-input"
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleGalleryPhotoUpload(e, false)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <button type="button" className="bg-white hover:bg-slate-100 text-slate-800 py-2.5 px-4 rounded-xl font-bold text-xs border border-slate-250 transition-colors shadow-sm flex items-center gap-2">
                          <HiOutlinePlus className="w-4 h-4" /> Choose Photo
                        </button>
                      </div>
                      {newGalleryItem.image ? (
                        <div className="relative w-28 h-20 bg-slate-900 border border-slate-200 rounded-xl overflow-hidden flex items-center justify-center shadow-sm">
                          <img
                            src={newGalleryItem.image}
                            alt="Upload preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">No Photo Loaded</div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button
                      type="submit"
                      disabled={isSavingGallery}
                      className="btn-green py-3 px-6 rounded-xl font-bold text-xs shadow-md shadow-primary/10 transition-all"
                    >
                      {isSavingGallery ? "Saving..." : "Register Gallery Item"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsAddingGallery(false)}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-705 py-3 px-6 rounded-xl font-bold text-xs transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* FULL PAGE EDIT GALLERY ITEM FORM */}
            {galleryToggle === 'images' && editingGalleryItem && (
              <div className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm max-w-4xl">
                <button
                  type="button"
                  onClick={() => setEditingGalleryItem(null)}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary font-bold mb-6 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition-all w-fit"
                >
                  <HiOutlineArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-6 pb-2 border-b border-slate-100">Edit Portfolio Image: {editingGalleryItem.title}</h3>
                <form onSubmit={handleSaveGalleryEdit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Image Title</label>
                      <input
                        type="text"
                        required
                        value={editingGalleryItem.title}
                        onChange={e => setEditingGalleryItem(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3.5 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Category / Division</label>
                      <select
                        value={editingGalleryItem.category}
                        onChange={e => setEditingGalleryItem(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3.5 px-4 text-xs outline-none focus:border-primary focus:bg-white text-slate-900 transition-all font-bold"
                      >
                        {galleryCategories.map(cat => (
                          <option key={cat.id} value={cat.slug}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Description</label>
                    <textarea
                      rows={3}
                      required
                      value={editingGalleryItem.desc}
                      onChange={e => setEditingGalleryItem(prev => ({ ...prev, desc: e.target.value }))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3.5 px-4 text-xs outline-none focus:border-primary focus:bg-white resize-none text-slate-900 transition-all leading-relaxed"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-widest">Photo (File Upload)</label>
                    <div className="flex flex-col sm:flex-row gap-6 items-center bg-slate-50 p-5 rounded-2xl border border-slate-200/60">
                      <div className="relative overflow-hidden">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleGalleryPhotoUpload(e, true)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <button type="button" className="bg-white hover:bg-slate-100 text-slate-800 py-2.5 px-4 rounded-xl font-bold text-xs border border-slate-250 transition-colors shadow-sm flex items-center gap-2">
                          <HiOutlinePlus className="w-4 h-4" /> Change Photo
                        </button>
                      </div>
                      {editingGalleryItem.image ? (
                        <div className="relative w-28 h-20 bg-slate-900 border border-slate-200 rounded-xl overflow-hidden flex items-center justify-center shadow-sm">
                          <img
                            src={editingGalleryItem.image}
                            alt="Upload preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">No Image Loaded</div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-slate-100">
                    <button
                      type="submit"
                      disabled={isSavingGallery}
                      className="btn-green py-3 px-6 rounded-xl font-bold text-xs shadow-md shadow-primary/10 transition-all"
                    >
                      {isSavingGallery ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingGalleryItem(null)}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-705 py-3 px-6 rounded-xl font-bold text-xs transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
